import { type ComponentType, type ReactNode, useState, useRef, useEffect, useMemo } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Popover from '@radix-ui/react-popover';
import * as SelectPrimitive from '@radix-ui/react-select';
import {
  Button,
  Calendar as CalendarView,
  Input,
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from '@ui';
import {
  BreakdownIcon,
  Calendar,
  Check,
  ChevronDown,
  Eye,
  EyeOff,
  GroupTypeFriends,
  GroupTypeHome,
  GroupTypeOffice,
  GroupTypeOthers,
  GroupTypeRoommates,
  GroupTypeTrip,
  LocationIcon,
  ReceiptIcon,
  Search,
  X,
  CloudUpload,
  OtherExpenseIcon,
  RepeatIcon2,
} from '@ui/icons';
import { useGroups, isActiveGroup, isViewOnlyMember, type GroupType } from '@entities/group';
import { useSession } from '@entities/session';
import { useCurrencies } from '@entities/currency';
import { useExpenseTypes, classifyExpense } from '@entities/expense-type';
import { useExchangeRate } from '@entities/exchange-rate';
import {
  usePendingExpenseAttachment,
  useGroupExpenseAttachment,
  recallAttachmentName,
} from '@entities/expense';
import { getCurrentPosition, reverseGeocode } from '@entities/location';
import type {
  BreakdownWireItem,
  ExpenseLocation,
  PaidByEntry,
  SaveBillConversion,
  SaveBillRecurrence,
  SplitEntry,
} from '@entities/expense';
import { cn, toMinorUnit, money, formatMoney, toShortName } from '@shared/lib';
import { toast } from '@shared/lib/toast';
import { t } from '@shared/i18n';
import { planUsageManager, FreeLimitType } from '@entities/plan-usage';
import { useSaveBill } from '../api/useSaveBill';
import { equalSplitMinor, convertPartsMinor, walletSplitDeductionMinor } from '../model/split';
import {
  RepeatModal,
  type RepeatConfig,
  type RepeatFrequency,
  type RepeatReminder,
} from './RepeatModal';
import { SplitModal, type SplitResultEntry } from './SplitModal';
import { PaidBySplitModal } from './PaidBySplitModal';
import { PaidByModal, type PaidByResultEntry } from './PaidByModal';
import { CategoryModal, type CategoryId } from './CategoryModal';
import { ReceiptUploadModal } from './ReceiptUploadModal';
import { BreakdownModal, type BreakdownItem } from './BreakdownModal';
import { assignedSharesToSplit, signedChargeAmount } from '../model/assignItems';
import { sortSplitMembers } from '../model/memberOrder';
import { LocationModal } from './LocationModal';

/** Date shown in the Date field trigger, e.g. "26-Aug 2026". */
const formatDate = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short' });
  return `${day}-${month} ${date.getFullYear()}`;
};

/** Local date → `yyyy-MM-dd` (the exchange-rate `date` query param format). */
const toYmd = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;

/**
 * Filters free-typed Amount input down to a valid number as the user types: digits and at most one
 * decimal point, capped at 9 integer digits and 2 decimal digits (mobile parity) — invalid
 * characters and over-limit digits are simply never entered rather than accepted then rejected.
 */
function sanitizeAmountInput(raw: string): string {
  let value = raw.replace(/[^\d.]/g, '');
  const firstDot = value.indexOf('.');
  if (firstDot !== -1) {
    value = value.slice(0, firstDot + 1) + value.slice(firstDot + 1).replace(/\./g, '');
  }
  const [intPart = '', decPart] = value.split('.');
  const clampedInt = intPart.slice(0, 9);
  return decPart !== undefined ? `${clampedInt}.${decPart.slice(0, 2)}` : clampedInt;
}

/**
 * Paid-by field glow (mobile parity): a RED halo when the wallet can't cover the bill (amount
 * exceeded), a BLUE halo when the signed-in user isn't the wallet's book-keeper. Each pairs a
 * slightly darker solid border with a layered halo that fades outward (a gradient-style glow),
 * blending from the border into a faint diffuse edge. Uses the mobile hex values directly (no token
 * exists for these functional indicator glows).
 */
// Identical halo geometry for both states — only the colour changes: a solid ring fading into a
// wider, faint diffuse edge (gradient-style glow).
const glowShadow = (r: number, g: number, b: number): string =>
  `0 0 0 1.5px rgba(${r}, ${g}, ${b}, 0.5), 0 0 6px 1px rgba(${r}, ${g}, ${b}, 0.3), 0 0 16px 5px rgba(${r}, ${g}, ${b}, 0.15)`;

const PAID_BY_GLOW: Record<'red' | 'blue', { border: string; shadow: string }> = {
  red: { border: '#E5484D', shadow: glowShadow(229, 72, 77) },
  blue: { border: '#3D74D9', shadow: glowShadow(61, 116, 217) },
};

/** Today at 00:00 (local) — for restricting the date calendar to today only. */
const startOfToday = (): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

/** File extension for a receipt's MIME type, when the URL itself doesn't carry a usable one. */
const RECEIPT_EXTENSION_BY_MIME: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/heic': 'heic',
  'image/heif': 'heif',
  'application/pdf': 'pdf',
};

/**
 * Filename for an existing receipt URL: the original name the user picked, when this browser
 * remembers it from the upload (see `attachmentNameCache`) — the backend stores the file under a
 * generated name (its own attachment/storage id) and never returns the original. Without a
 * remembered name, NEVER surface that generated id to the user (it's an internal record
 * identifier — showing it is an information-disclosure smell, not just an ugly filename): fall
 * back to a generic display name instead, keeping only the file extension (harmless) from the URL
 * or, failing that, the MIME type.
 */
const receiptFileNameFromUrl = (url: string, mimeType?: string): string => {
  const remembered = recallAttachmentName(url);
  if (remembered) return remembered;
  const seg = url.split('/').pop()?.split('?')[0] ?? '';
  const dot = seg.lastIndexOf('.');
  const urlExt = dot > -1 ? seg.slice(dot + 1).toLowerCase() : '';
  const mimeExt = RECEIPT_EXTENSION_BY_MIME[mimeType?.toLowerCase() ?? ''];
  const ext = urlExt !== '' ? urlExt : mimeExt ?? 'jpg';
  return `${t.newBill.attachmentGenericName}.${ext}`;
};

/** Decode a base64 payload (with or without a `data:` prefix) into a `File` for preview/replace. */
const base64ToFile = (base64: string, contentType: string, name: string): File => {
  const raw = base64.includes(',') ? base64.split(',')[1] ?? '' : base64;
  const binary = atob(raw);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], name, { type: contentType || 'application/octet-stream' });
};

/** Group-type → avatar icon (matches the rail). */
const TYPE_ICONS: Record<GroupType, ComponentType<{ className?: string }>> = {
  home: GroupTypeHome,
  trip: GroupTypeTrip,
  friends: GroupTypeFriends,
  office: GroupTypeOffice,
  roommates: GroupTypeRoommates,
  others: GroupTypeOthers,
};

/** Default currency code selected when the modal opens (until the user picks another). */
const DEFAULT_CURRENCY_CODE = 'USD';

/**
 * Prefill for EDIT mode — the values pulled from a saved (pending) expense. Group + note aren't in
 * the pending-expense list payload, so they're not prefilled; `currencyCode` / `categoryName` are
 * resolved to ids against the loaded currency / expense-type lists.
 */
export interface NewBillEditValues {
  /** Existing expense id → the save updates instead of creating. */
  readonly id: string;
  /**
   * True when editing a COMMITTED group-ledger expense (from the group Expenses tab) — routes the
   * update to `PUT /api/v1/expense/{groupId}/{id}` rather than the pending-expense endpoint, even if
   * the recurrence was removed. Omitted for pending (needs-attention) expenses.
   */
  readonly isGroupExpense?: boolean;
  /** Major-unit amount string, e.g. "22.00" ('' when not yet entered). */
  readonly amount: string;
  readonly description: string;
  readonly date: Date | null;
  readonly isPrivate: boolean;
  /** Currency ISO code — resolved to a currency id against the loaded list. */
  readonly currencyCode: string;
  /** Category label — resolved to an expense-type id against the loaded list. */
  readonly categoryName: string;
  /** Group id, when known (group-ledger edit). Omitted for unassigned pending rows. */
  readonly groupId?: string;
  /** Free-text note, when available. */
  readonly note?: string;
  /** Existing receipt attachment URL, when the expense already has one. */
  readonly attachmentUrl?: string;
  /** Recurrence rule to seed the Repeat modal, when the expense repeats. */
  readonly recurrence?: {
    readonly frequency: RepeatFrequency;
    readonly every: number;
    readonly date: Date | null;
    readonly reminder: RepeatReminder;
    readonly endRepeat?: 'never' | 'onDate';
    readonly endDate?: Date | null;
  };
  /** The expense's SAVED top-level `split_type` — re-sent verbatim on an edit whose split UI was
   *  never in play (private expenses), so the update can't fabricate a split-type diff. */
  readonly storedSplitType?: string;
  /** Participants the saved expense references (payments + splits), with names/avatars resolved
   *  from the transaction itself — backfills Paid-by/Split rows for users the GROUP no longer
   *  lists (deleted/removed members), whose names otherwise rendered blank in edit mode. */
  readonly participants?: readonly { id: string; name: string; avatarUrl: string | null }[];
  /** Saved Paid-by legs (in the distribution currency), to prefill the Paid-by field on edit. */
  readonly paidBy?: readonly PaidByResultEntry[];
  /** Saved Split shares (in the distribution currency), to prefill the Split field on edit. */
  readonly split?: readonly SplitResultEntry[];
  /** Saved breakdown rows (line items + additional charges) to prefill the breakdown modal on edit.
   *  `price` is the row's MAJOR-unit MAGNITUDE — a charge's sign follows its role (a Discount
   *  subtracts; see `signedChargeAmount`), the same way it is entered. */
  readonly breakdown?: readonly {
    readonly name: string;
    readonly price: string;
    readonly type: 'item' | 'charge';
    /** The saved sign — restores a free-sign charge's ("Other") + / − choice. */
    readonly sign?: '+' | '-';
  }[];
  /** Saved location, to prefill the location picker chip on edit. */
  readonly location?: ExpenseLocation | null;
}

export interface NewBillModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, the modal opens in EDIT mode — fields prefilled + the save PATCHes this expense. */
  initial?: NewBillEditValues;
  /**
   * Pre-select this group in a NEW (non-edit) bill — e.g. the global "Add expense" button opening
   * while the user is already viewing a specific group's page. Ignored once `initial` is set (the
   * edit prefill's own `groupId` wins), and re-applied every time the modal is reopened.
   */
  initialGroupId?: string;
}

/**
 * Currency flag. The currencies API gives only an `emoji` + `iso2` (no flag image), and emoji flags
 * DON'T render on Windows — so we show a real flag image derived from the ISO2 code (or an API-
 * provided base64/URL image when present). On image error we fall back to the emoji, then the code.
 *
 * NOTE: flag images load from flagcdn.com. In dev that's fine; for production, allow `flagcdn.com`
 * in the CSP `img-src` (or self-host the flags).
 */
function CurrencyFlag({
  flagImage,
  flag,
  iso2,
  className,
}: {
  flagImage: string | null;
  flag: string;
  iso2: string;
  className?: string;
}) {
  const [imgFailed, setImgFailed] = useState(false);
  const src = flagImage ?? (iso2 ? `https://flagcdn.com/${iso2.toLowerCase()}.svg` : null);

  if (src && !imgFailed) {
    return (
      <img
        src={src}
        alt=""
        className={cn('h-4 w-6 rounded-[2px] object-cover', className)}
        onError={() => setImgFailed(true)}
      />
    );
  }
  if (flag) {
    return (
      <span className={cn('text-base leading-none', className)} aria-hidden>
        {flag}
      </span>
    );
  }
  return (
    <span className={cn('text-[10px] font-semibold text-muted-soft', className)} aria-hidden>
      {iso2}
    </span>
  );
}

/** Field label row with an optional right-aligned hint (e.g. exchange value / "Recurrence"). */
function FieldLabel({ children, hint }: { children: ReactNode; hint?: ReactNode }) {
  return (
    <div className="flex items-end justify-between gap-2">
      <span className="text-base font-medium text-fg">{children}</span>
      {hint}
    </div>
  );
}

/**
 * "New Bill" expense modal (Figma) — opened from the header Add-expense button. A dense, responsive
 * form (full-screen on mobile, large centered card on desktop). Presentational for now; Save/Cancel
 * close the modal. All copy from `t.newBill`.
 */
export function NewBillModal({ open, onOpenChange, initial, initialGroupId }: NewBillModalProps) {
  const m = t.newBill;
  // EDIT mode when an `initial` prefill is supplied. Callers key the modal by the edited id so these
  // initial-state seeds re-run per edit target (a fresh mount each time).
  const [visibility, setVisibility] = useState<'group' | 'private'>(
    initial?.isPrivate ? 'private' : 'group',
  );
  // Reuse the My Groups list cache (no extra API).
  const { data: groups } = useGroups();
  const { data: user } = useSession();
  const [groupId, setGroupId] = useState(initial?.groupId ?? initialGroupId ?? '');
  // "New Bill for" offers ACTIVE groups — plus the group the bill is already on when that one is
  // ARCHIVED (opened from an archived group's own page, or editing one of its expenses). Filtering
  // to active-only used to leave the header blank there: `initialGroupId` was set, but the id
  // matched no option, so the trigger showed the placeholder (and Save then demanded a group).
  // Archived groups still accept expenses (mobile parity); CLOSED ones never do (read-only, and
  // AppShell refuses to open this modal for them), so a closed group is never offered.
  const selectableGroups = (groups ?? []).filter(
    (g) => isActiveGroup(g) || (g.id === groupId && !g.isClosed),
  );
  // This modal instance stays mounted across navigation (AppShell renders one, shared by every
  // page), so `initialGroupId` (the group currently open in the rail) can change while it's
  // closed — re-sync on every OPEN transition (not just at mount) so a NEW bill always starts on
  // whichever group the user is looking at, not whatever was last selected. Skipped for an edit
  // prefill (`initial`), which owns its own groupId and is remounted fresh per edit target anyway.
  // Adjusting state during render (not a useEffect) per the React-recommended pattern for
  // "state that resets when a prop changes" — avoids an extra commit + cascading re-render.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open && !initial) setGroupId(initialGroupId ?? '');
  }
  // Date defaults to today (create), or the expense's date in edit mode.
  const [date, setDate] = useState<Date | undefined>(initial?.date ?? new Date());
  // Today (00:00) — the only selectable day in the date calendar.
  const calendarToday = startOfToday();
  const [dateOpen, setDateOpen] = useState(false);
  const [repeat, setRepeat] = useState<RepeatConfig | null>(
    initial?.recurrence
      ? {
          frequency: initial.recurrence.frequency,
          every: initial.recurrence.every,
          date: initial.recurrence.date ?? undefined,
          endRepeat: initial.recurrence.endRepeat ?? 'never',
          endDate: initial.recurrence.endDate ?? undefined,
          reminder: initial.recurrence.reminder,
        }
      : null,
  );
  const [repeatOpen, setRepeatOpen] = useState(false);
  const repeatLabel = repeat ? m.repeat.frequencies[repeat.frequency] : m.repeat.none;
  const [amount, setAmount] = useState(initial?.amount ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [note, setNote] = useState(initial?.note ?? '');
  // Footer-level validation error (save-chain guards that aren't tied to a single input: group /
  // currency / paid-by / split / conversion / breakdown).
  const [formError, setFormError] = useState<string | null>(null);
  // Field-level validation messages, rendered inline BELOW the matching input (red border + text).
  // A present string both flags the field red (aria-invalid) and prints the message under it.
  const [fieldErrors, setFieldErrors] = useState<{
    amount?: string;
    description?: string;
    group?: string;
  }>({});
  const [splitOpen, setSplitOpen] = useState(false);
  const [paidByOpen, setPaidByOpen] = useState(false);
  // User overrides from the Split / Paid-by modals (null ⇒ use the auto defaults: equal split,
  // current user pays all). Reset when the group or amount changes so they never go stale. In EDIT
  // mode they're seeded from the saved expense so the Paid-by / Split fields reflect what was saved.
  // Whether the current `splitOverride` was DERIVED from the Breakdown's "Assign each item" step
  // (vs. configured by hand in the Split field's modal / seeded from an edit). Only such a derived
  // split is voided when the user later picks "Split total (evenly)" in the breakdown flow.
  const [splitFromAssignment, setSplitFromAssignment] = useState(false);
  const [splitOverride, setSplitOverride] = useState<SplitResultEntry[] | null>(
    initial?.split ? initial.split.map((s) => ({ ...s })) : null,
  );
  const [paidByOverride, setPaidByOverride] = useState<PaidByResultEntry[] | null>(
    initial?.paidBy ? initial.paidBy.map((p) => ({ ...p })) : null,
  );
  // A members-only multi-payer pick chains into `PaidBySplitModal` (see the Paid-by modal's
  // onSave). If the user CANCELS that chained Split without saving, the paid-by change is undone
  // (reverts to the prior payer — e.g. the default wallet), so the field never gets stuck on
  // "Multiple". Tracked with refs (not state) so the save-vs-cancel decision reads synchronously
  // within the close handler.
  const splitChainPendingRef = useRef(false);
  const paidByBeforeChainRef = useRef<PaidByResultEntry[] | null>(null);
  // The currency pick that opened the "Set Currency preference" popup — restored if it's dismissed
  // via X/overlay/Escape (Radix's own dismiss path, not the explicit "Set as" / "Not Now" choices,
  // which manage `currencyConversionPopupOpen` directly and never touch this ref).
  const previousCurrencyIdRef = useRef('');
  // Payer ids for the OPEN `PaidBySplitModal` (the Split popup chained from a multi-payer
  // Paid-by save) — non-null means that modal is open. A SEPARATE modal + state from the Split
  // field's own `SplitModal` (`splitOpen`), so the Paid-by selection can never leak into the
  // field's whole-group flow (or vice versa).
  const [chainPayerIds, setChainPayerIds] = useState<readonly string[] | null>(null);
  const [categoryOpen, setCategoryOpen] = useState(false);
  // Category (expense type) — holds the backend `_id`; the list comes from `/api/v1/expense-type`.
  const [category, setCategory] = useState<CategoryId | undefined>(undefined);
  // Once the user hand-picks a category (or we prefill one in edit mode), stop the description
  // auto-classify from overwriting it (mobile `setExpenseTypeManual`).
  const [categoryManual, setCategoryManual] = useState(Boolean(initial?.categoryName));
  const { data: expenseTypes = [] } = useExpenseTypes();
  // Edit mode: resolve the prefilled category NAME → id (DERIVED, not state, so no setState-in-effect).
  // A user pick (`category`) always takes precedence over the edit prefill.
  const editCategoryId = initial?.categoryName
    ? expenseTypes.find((e) => e.name === initial.categoryName)?.id
    : undefined;
  const effectiveCategory = category ?? editCategoryId;
  const selectedCategory = expenseTypes.find((e) => e.id === effectiveCategory);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  // An EXISTING receipt URL (edit mode) — kept until the user replaces or removes the attachment.
  // Preserved on save (via `receiptUrl`) so editing without touching it doesn't drop the receipt.
  const [receiptUrl, setReceiptUrl] = useState(initial?.attachmentUrl ?? '');
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);
  const [location, setLocation] = useState<ExpenseLocation | null>(initial?.location ?? null);
  // Auto-attach the current location to a NEW bill on open (mobile parity: GroupBillScreen's
  // `_autoFetchAndSetLocation`) — best-effort and SILENT: a denied permission or failed lookup
  // simply leaves the field empty, never toasts, never blocks the form. Skipped for edits, and
  // for users whose `auto_attach_location` preference is explicitly OFF. `cur ?? place` means a
  // location the user picks while the fetch is in flight always wins; the cancelled flag keeps
  // StrictMode's dev double-invoke and a mid-fetch close from applying a stale result.
  const autoAttachLocation = user?.autoAttachLocation !== false;
  const autoLocationRunRef = useRef(0);
  useEffect(() => {
    if (!open || initial || !autoAttachLocation) return;
    const runId = ++autoLocationRunRef.current;
    const isStale = () => autoLocationRunRef.current !== runId;
    void (async () => {
      try {
        const pos = await getCurrentPosition();
        if (!pos.ok || isStale()) return;
        // NO IP fallback here (unlike the picker): silently attaching a city-level guess to an
        // expense would be wrong data — mobile only auto-attaches a real device fix too.
        const place = await reverseGeocode(pos.value.lat, pos.value.long);
        if (!place || isStale()) return;
        setLocation((cur) => cur ?? place);
      } catch {
        // Best-effort — swallow (mobile parity: auto-location must never disrupt the screen).
      }
    })();
    return () => {
      autoLocationRunRef.current += 1; // invalidate this run on close/re-open (and StrictMode)
    };
  }, [open, initial, autoAttachLocation]);
  // The receipt chip reads "attached" when there's a new file OR an existing URL.
  const hasReceipt = receiptFile !== null || receiptUrl !== '';
  // Edit mode: fetch the EXISTING receipt binary so "View Attachment" can preview it — LAZILY, only
  // once the user actually opens the receipt modal (`receiptModalOpen`), not the moment the edit form
  // loads. Opening an edit used to always fire this (base64) fetch in the background even when the
  // user never looked at the attachment, adding a needless API call + payload to every edit open. A
  // committed group-ledger edit uses the GROUP endpoint (`/api/v1/expense/{id}/attachment`); a
  // pending (needs-attention) edit uses the pending one. Both hooks are called (one enabled) — hooks
  // can't be called conditionally.
  const isGroupEdit = Boolean(initial?.isGroupExpense);
  const attachmentEnabled =
    Boolean(initial?.id) && receiptUrl !== '' && receiptFile === null && receiptModalOpen;
  const groupAttachmentQuery = useGroupExpenseAttachment(
    initial?.groupId ?? '',
    initial?.id ?? '',
    attachmentEnabled && isGroupEdit,
  );
  const pendingAttachmentQuery = usePendingExpenseAttachment(
    initial?.id ?? '',
    attachmentEnabled && !isGroupEdit,
  );
  const attachmentQuery = isGroupEdit ? groupAttachmentQuery : pendingAttachmentQuery;
  // The fetched attachment as a File (for the preview modal). Rebuilt when the bytes/URL change.
  // Gated on `attachmentEnabled` (not just `attachmentQuery.data`): React Query keeps the LAST
  // fetched binary cached even after the query is disabled, so once the user removes the existing
  // attachment (clearing `receiptUrl`, which flips `attachmentEnabled` false), the stale cached
  // bytes would otherwise resurface here — reopening "Attach Receipt" showed the just-removed file
  // again (under the generic fallback name, since `receiptFileNameFromUrl('')` has no URL segment
  // to read), and clicking Upload on that stale preview silently re-attached it.
  const fetchedReceiptFile = useMemo(() => {
    if (!attachmentEnabled) return null;
    const data = attachmentQuery.data;
    if (!data) return null;
    return base64ToFile(
      data.base64,
      data.contentType,
      receiptFileNameFromUrl(receiptUrl, data.contentType),
    );
  }, [attachmentEnabled, attachmentQuery.data, receiptUrl]);
  // Currency list from the backend (`GET /api/v1/geo/currencies`); selection holds the backend `_id`
  // (sent as `currency_id`). Hardcoded mock currencies sent an invalid id → the create 400'd.
  const { data: currencies = [] } = useCurrencies();
  const [currencyId, setCurrencyId] = useState('');
  // Seed the breakdown rows from a saved expense (edit mode): line items vs additional charges are
  // split by their `type`; `price` is the major-unit magnitude. Empty for a fresh bill.
  const [breakdownItems, setBreakdownItems] = useState<BreakdownItem[]>(() =>
    (initial?.breakdown ?? [])
      .filter((b) => b.type === 'item')
      .map((b, i) => ({ id: `edit-item-${i}`, name: b.name, price: b.price })),
  );
  const [breakdownFees, setBreakdownFees] = useState<BreakdownItem[]>(() =>
    (initial?.breakdown ?? [])
      .filter((b) => b.type === 'charge')
      .map((b, i) => ({
        id: `edit-fee-${i}`,
        name: b.name,
        price: b.price,
        ...(b.sign ? { sign: b.sign } : {}),
      })),
  );
  const [breakdownModalOpen, setBreakdownModalOpen] = useState(false);
  const [currencySearch, setCurrencySearch] = useState('');
  const [currencySelectOpen, setCurrencySelectOpen] = useState(false);
  const currencySearchInputRef = useRef<HTMLInputElement>(null);
  const [currencyConversionPopupOpen, setCurrencyConversionPopupOpen] = useState(false);
  // Whether the user confirmed "Set as {currency}" — sent as save_preferred_currency on save.
  const [savePreferredCurrency, setSavePreferredCurrency] = useState(false);

  useEffect(() => {
    if (currencySelectOpen && document.activeElement !== currencySearchInputRef.current) {
      currencySearchInputRef.current?.focus();
    }
  }, [currencySearch, currencySelectOpen]);

  // Auto-classify the category from the description (mobile: 300ms debounce → classify-expense API).
  // Skipped once the user hand-picks a category (`categoryManual`) or the description is empty. A
  // request-id guard drops stale responses so a slow classify can't overwrite a newer one.
  const classifyReqId = useRef(0);
  useEffect(() => {
    const text = description.trim();
    if (categoryManual || text === '') return;
    const handle = setTimeout(() => {
      const reqId = ++classifyReqId.current;
      classifyExpense(text)
        .then((suggested) => {
          if (!suggested || reqId !== classifyReqId.current) return;
          // Only apply a suggestion that exists in the loaded list (so the id maps to a real type).
          // Match by id first; a response that carries only a NAME (or an id in a different
          // format) still resolves by name — before, that was silently dropped and the icon never
          // followed the description.
          const wantedName = suggested.name.trim().toLowerCase();
          const match =
            expenseTypes.find((e) => suggested.id !== '' && e.id === suggested.id) ??
            (wantedName
              ? expenseTypes.find((e) => e.name.trim().toLowerCase() === wantedName)
              : undefined);
          if (match) setCategory(match.id);
        })
        .catch(() => {
          /* classify is best-effort — a failure just leaves the category unchanged */
        });
    }, 300);
    return () => clearTimeout(handle);
  }, [description, categoryManual, expenseTypes]);

  // Default currency until the user picks one — DERIVED, no state sync (so we never call setState in
  // an effect). Preference order: the user's saved currency (login `currency_id`) → USD → first.
  // `effectiveCurrencyId` is what the field shows + submits.
  const userCurrencyId = user?.defaultCurrencyId ?? '';
  const defaultCurrencyId = useMemo(() => {
    const byUser = userCurrencyId ? currencies.find((c) => c.id === userCurrencyId) : undefined;
    const fallback =
      byUser ?? currencies.find((c) => c.code === DEFAULT_CURRENCY_CODE) ?? currencies[0];
    return fallback?.id ?? '';
  }, [currencies, userCurrencyId]);
  // Edit mode: the expense's currency (resolved from its ISO code) takes precedence over the user's
  // home default — but a manual pick (`currencyId`) still wins. Only while the group dropdown still
  // matches the expense's ORIGINAL group, though — this is a static, non-reactive value (derived once
  // from `initial`, not from the current `groupId`), so without this guard changing the group during
  // an edit had no effect on currency at all: it kept losing out to the old expense's currency instead
  // of falling through to the newly-selected group's own preferred/native currency below.
  const editCurrencyId =
    initial?.currencyCode && groupId === (initial.groupId ?? '')
      ? currencies.find((c) => c.code === initial.currencyCode)?.id ?? ''
      : '';
  // When a group is selected, its `user_preferred_currency_id` (the currency the user last entered
  // expenses in for that group) is the default bill currency — so a second expense defaults to it.
  const selectedGroupForCurrency = selectableGroups.find((g) => g.id === groupId);
  const groupPreferredCurrencyId = selectedGroupForCurrency?.preferredCurrencyId ?? '';
  // A group's OWN currency (what it's denominated in) is the next fallback — for a group the user
  // hasn't personally entered an expense in yet (no `user_preferred_currency_id`), the bill should
  // default to what the GROUP itself is set up in, not fall straight through to the signed-in user's
  // unrelated home/profile currency below.
  const groupCurrencyId = selectedGroupForCurrency
    ? currencies.find((c) => c.code === selectedGroupForCurrency.currency)?.id ?? ''
    : '';
  const effectiveCurrencyId =
    currencyId ||
    editCurrencyId ||
    groupPreferredCurrencyId ||
    groupCurrencyId ||
    defaultCurrencyId;
  const selectedCurrency = currencies.find((c) => c.id === effectiveCurrencyId);

  // Total + members for the Split modal (falls back to the first active group when none is picked).
  let totalMinor = 0;
  try {
    totalMinor = toMinorUnit(amount);
  } catch {
    totalMinor = 0;
  }
  // Paid-by / Split need BOTH a selected group AND a positive amount. They're disabled (dimmed) until
  // both are set; tapping while disabled shows the right validation instead of opening the modal
  // (missing amount → inline red under the amount field; missing group → footer "Please select group").
  const amountMissing = totalMinor <= 0;
  const paidBySplitDisabled = !groupId || amountMissing;
  const requirePaidBySplit = (openModal: () => void) => () => {
    if (amountMissing) {
      setFormError(null);
      setFieldErrors({ amount: m.validation.enterAmount });
      return;
    }
    if (!groupId) {
      setFormError(null);
      setFieldErrors({ group: m.validation.selectGroup });
      return;
    }
    openModal();
  };
  // Add Breakdown needs a valid amount first — tapping it empty flags the amount field instead. A
  // PRIVATE expense has no shared breakdown, so the control is inert.
  const openBreakdown = () => {
    if (visibility === 'private') return;
    if (amountMissing) {
      setFormError(null);
      setFieldErrors({ amount: m.validation.enterAmount });
      return;
    }
    setBreakdownModalOpen(true);
  };
  // ONLY the selected group — no fallback to the first group in the list. That fallback made a
  // bill with NO group selected (or one whose group wasn't offered) silently borrow an unrelated
  // group's wallet + members: the "Wallet Balance:" hint showed up under Amount with the group
  // field still empty. Every consumer below already handles `undefined` (no members, no wallet,
  // home currency falls back to the user's own).
  const splitGroup = selectableGroups.find((g) => g.id === groupId);
  const unorderedMembers = (splitGroup?.members ?? []).map((mem) => ({
    id: mem.id,
    // Unregistered/guest members can have no name at all — fall back to their phone number so
    // Paid-by/Split/Breakdown never show a blank label (the phone is still shown separately when
    // a real name exists; see PaidByModal's secondary phone span). `.trim()` matters here: some
    // backend rows send a whitespace-only name (e.g. " ") rather than '' — that's truthy to `||`,
    // so it would win over the phone fallback and render as a blank label with no visible text.
    // A real name is shortened to "First L" (mobile's `shortName` convention); a phone has no
    // space, so it passes through toShortName unchanged.
    name: mem.name.trim() ? toShortName(mem.name) : mem.phone,
    avatarUrl: mem.avatarUrl,
    phone: mem.phone,
    email: mem.email ?? '',
    isYou: Boolean(user?.id) && mem.id === user?.id,
  }));
  // EDIT mode: users the saved expense references but the group no longer lists (deleted or
  // removed members) still need display rows — without them the Paid-by/Split screens and the
  // payer label rendered a blank/fallback name for the very leg being edited. Names come from
  // the saved expense's own resolved participants (already "First L (Deleted User)"-formatted).
  {
    const knownMemberIds = new Set(unorderedMembers.map((mem) => mem.id));
    for (const p of initial?.participants ?? []) {
      if (p.id && !knownMemberIds.has(p.id)) {
        knownMemberIds.add(p.id);
        unorderedMembers.push({
          id: p.id,
          name: p.name,
          avatarUrl: p.avatarUrl,
          phone: '',
          email: '',
          isYou: false,
        });
      }
    }
  }
  // ONE order for every split/paid-by screen AND the wire's default equal split — mobile's: You
  // first, then names A→Z, then phone-only members. The split engine's remainder rules are
  // order-based, so listing members in the backend's order gave different per-member amounts
  // than mobile for the same Split by amount / percentage.
  const splitMembers = sortSplitMembers(unorderedMembers);
  // A PRIVATE expense involves only the payer — so it has no shared Split, Paid-by, or Breakdown:
  // those controls are disabled. Split is ALSO disabled for a single-member group (nothing to split).
  const isPrivateExpense = visibility === 'private';
  const singleMemberGroup = Boolean(groupId) && splitMembers.length <= 1;
  // Mobile parity (`isPaidByEnable`): the group's wallet FEATURE (not its balance — even a ₹0 wallet
  // still counts as a pickable option, see PaidByModal's own insufficient-balance handling), separate
  // from `groupWalletMinor` below which collapses "no wallet" and "zero balance" to the same 0.
  const hasGroupWallet = splitGroup?.walletBalance != null && !splitGroup.isWalletClosed;
  // Paid-by needs someone to actually pick as payer — another member besides you, or the wallet. A
  // single-member group with no wallet has no valid payer at all.
  const hasPayerOption = splitMembers.length > 1 || hasGroupWallet;
  const splitDisabled = paidBySplitDisabled || isPrivateExpense || singleMemberGroup;
  const paidByDisabled = paidBySplitDisabled || isPrivateExpense || !hasPayerOption;
  const breakdownDisabled = isPrivateExpense;
  // Split trigger label reflects the saved split MODE only ("Evenly" by default, else Shares /
  // Percentage / Amount from the override's split_type). `splitOverride` is written ONLY by the
  // Split field's own modal, an edit seed, or the Breakdown's "Assign each item" step (as a split
  // by Amount) — the Paid-by chained popup configures the PAYERS and never touches it, so a
  // paid-by method can't leak into this field.
  const splitButtonLabel = (() => {
    switch (splitOverride?.[0]?.splitType) {
      case 'Shares':
        return m.splitShares;
      case 'Percentage':
        return m.splitPercentage;
      case 'Amount':
        return m.splitAmount;
      default:
        return m.splitEvenly;
    }
  })();

  const filteredCurrencies = currencies
    .filter(
      (row) =>
        row.code.toLowerCase().includes(currencySearch.toLowerCase()) ||
        row.name.toLowerCase().includes(currencySearch.toLowerCase()),
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  // ---- Currency conversion (bill currency ≠ the GROUP's settlement currency) ----
  // The reference is the GROUP's OWN currency (what its ledger/wallet actually settle in) — NOT the
  // signed-in user's personal profile currency, which this used to compare against by mistake. That
  // meant picking a bill currency that correctly matched the group's own — just not the user's
  // unrelated personal preference — still fired the "Current exchange value" hint, the Free-plan
  // conversion gate, and the "Set Currency preference" popup below, and (more seriously) sent the
  // WRONG currency as `groupCurrencyId` in the save payload's conversion block for a wallet-funded,
  // converted expense. A group-less expense (private, no group picked yet) has no group settlement
  // currency to reference, so that case alone still falls back to the user's own currency.
  // When the user picks a DIFFERENT bill currency, we fetch the single-date rate (API 9: GET
  // exchange-rate/rate?base=<bill>&quotes=<home>&date=<expense date>) and show the entered amount
  // converted INTO the home currency (home symbol) as the "Current exchange value:" hint.
  const homeCurrency = splitGroup
    ? currencies.find((c) => c.code === splitGroup.currency)
    : currencies.find((c) => c.id === defaultCurrencyId);
  const homeCurrencyCode = homeCurrency?.code ?? '';
  const homeCurrencySymbol = homeCurrency?.symbol ?? '';
  const homeCurrencyId = homeCurrency?.id ?? '';
  const billCurrencyCode = selectedCurrency?.code ?? '';
  const conversionActive =
    Boolean(billCurrencyCode) && Boolean(homeCurrencyCode) && billCurrencyCode !== homeCurrencyCode;
  const expenseDate = date ?? new Date();
  // The FX rate exists only up to TODAY — a FUTURE (e.g. recurring) expense date has no rate yet, so
  // request the rate for today (the date the expense is created with). A past/today date keeps its own
  // historical rate. This also means picking a future date in the calendar doesn't fire a rate call
  // for that date (the requested date stays today), matching the mobile behaviour.
  const todayYmd = toYmd(new Date());
  const expenseYmd = toYmd(expenseDate);
  const fxDateYmd = expenseYmd > todayYmd ? todayYmd : expenseYmd;
  const { data: exchangeRate } = useExchangeRate(
    conversionActive ? billCurrencyCode : '',
    conversionActive ? homeCurrencyCode : '',
    conversionActive ? fxDateYmd : '',
  );
  const amountNumber = Number(amount.trim());
  const convertedMajor =
    conversionActive && exchangeRate && Number.isFinite(amountNumber) && amountNumber > 0
      ? amountNumber * exchangeRate.rate
      : null;
  // e.g. "₹2,560.50" — home-currency symbol + the converted amount (2dp, grouped).
  const convertedDisplay =
    convertedMajor !== null
      ? `${homeCurrencySymbol}${convertedMajor.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`
      : null;

  // The rate that maps a home minor amount back to the bill currency (for the wire `*_amount`), and
  // the entered bill total converted INTO the home/group currency.
  const conversionRate = conversionActive && exchangeRate ? exchangeRate.rate : 0;
  const convertedTotalMinor = conversionRate > 0 ? Math.round(totalMinor * conversionRate) : 0;

  const groupWalletMinor = hasGroupWallet ? splitGroup.walletBalance : null;
  // EDIT mode: the wallet's already-recorded contribution to THIS expense is still available to
  // it — the current balance no longer includes that spent amount, so without adding it back,
  // editing an older wallet-paid expense (balance since drained) would silently drop the wallet
  // payer and re-split its share onto the members. The backend reconciles the difference on
  // update. Zero for a fresh bill, so the create flow is untouched.
  const editWalletPaidMinor =
    initial?.paidBy?.find((p) => p.type === 'wallet')?.paidAmountMinor ?? 0;
  // The wallet's usable CAPACITY for this bill — use this (not the raw balance) wherever the
  // wallet's contribution is capped or its payability decided.
  const effectiveWalletMinor: number | null = hasGroupWallet
    ? splitGroup.walletBalance + editWalletPaidMinor
    : editWalletPaidMinor > 0
    ? editWalletPaidMinor
    : null;
  // Who pays decides the currency the split / paid-by distribution runs in (all Split-modal tabs
  // compute on it):
  //  • the WALLET pays → the HOME/group currency (the wallet holds and settles in it), so — when a
  //    conversion is active — the tabs compute on the CONVERTED (home) total.
  //  • You / a member pays → the SELECTED bill currency; the tabs compute on the entered amount, and
  //    the group-currency legs (`group_*_amount`) are derived at save time.
  // Default (no override): the wallet is the payer whenever the group has a funded wallet and the bill
  // isn't private (same condition as `defaultPaidBy` below); an override is wallet-based when it
  // carries a wallet leg.
  const paysFromWallet =
    paidByOverride != null
      ? paidByOverride.some((p) => p.type === 'wallet')
      : effectiveWalletMinor !== null && effectiveWalletMinor > 0 && visibility !== 'private';
  // Home-currency distribution applies only when the wallet pays AND a conversion is active.
  const useHomeDistribution = paysFromWallet && convertedTotalMinor > 0;
  const distributionTotalMinor = useHomeDistribution ? convertedTotalMinor : totalMinor;
  const distributionCurrencyCode = useHomeDistribution
    ? homeCurrencyCode
    : selectedCurrency?.code ?? 'USD';

  // The wallet's balance ALWAYS sits in the group's home currency (`groupWalletMinor`) — the
  // "Wallet Balance:" hint under the Amount field always shows that figure first (unchanged from
  // before). Mobile parity: once a DIFFERENT bill currency is picked, it also shows the balance
  // CONVERTED into that currency in parentheses, e.g. "Wallet Balance: ₹674.00 ($7.10)". This
  // used to be missing — the hint's symbol followed `distributionCurrencyCode` (the bill currency
  // until an amount is typed) while the number stayed the raw home balance, i.e. only the symbol
  // "converted". `conversionRate` is "home per 1 bill" (see `convertedMajor` above), so dividing
  // the home balance by it converts the other way, into the bill currency. Left out while the
  // rate hasn't loaded yet (`conversionRate <= 0`) rather than showing a stale/zero figure.
  const walletBalanceConvertedMinor =
    groupWalletMinor !== null &&
    billCurrencyCode &&
    billCurrencyCode !== homeCurrencyCode &&
    conversionRate > 0
      ? Math.round(groupWalletMinor / conversionRate)
      : null;

  // Paid-by field glow (mobile parity) — only when the WALLET pays and the field is enabled:
  //  • RED when the bill EXCEEDS the wallet balance (the wallet can't cover it), else
  //  • BLUE when the signed-in user is NOT the wallet's book-keeper (an FYI hint).
  // The wallet balance and the distribution total are both in the home/group currency.
  const walletBookkeeperIds = splitGroup?.walletBookkeeperIds ?? [];
  const isNotBookKeeper =
    walletBookkeeperIds.length > 0 && !walletBookkeeperIds.includes(user?.id ?? '');
  // What the current selection actually covers — NOT just the wallet's own balance. Comparing the
  // whole bill against the wallet alone meant picking "Wallet + a member" to cover the shortfall
  // never cleared the red glow, since the bill still "exceeded the wallet" on its own. When there's
  // an explicit override, sum every leg (wallet + members); otherwise mirror the default payer's
  // own wallet-only leg (`defaultPaidBy` below), computed inline here to avoid a forward reference.
  // A LONE wallet override is the one exception: its captured `paidAmountMinor` is a SNAPSHOT from
  // whenever the payer was last picked (edit-mode seed, or an earlier amount) — editing a
  // wallet-paid expense's amount without reopening the Paid-by field kept comparing against that
  // stale figure, showing the red "can't cover it" glow even once the wallet's current usable
  // capacity (`effectiveWalletMinor`, which restores THIS expense's own prior wallet contribution)
  // could fully cover the new total. Reconciled against the live total instead.
  const isLoneWalletOverride = paidByOverride?.length === 1 && paidByOverride[0]?.type === 'wallet';
  const totalCoveredMinor = isLoneWalletOverride
    ? Math.min(effectiveWalletMinor ?? 0, distributionTotalMinor)
    : paidByOverride
    ? paidByOverride.reduce((sum, p) => sum + p.paidAmountMinor, 0)
    : Math.min(effectiveWalletMinor ?? 0, distributionTotalMinor);
  const isAmountExceeded = paysFromWallet && totalCoveredMinor < distributionTotalMinor;
  const paidByGlow: 'red' | 'blue' | null = (() => {
    if (paidByDisabled || !paysFromWallet) return null;
    if (isAmountExceeded) return 'red';
    if (isNotBookKeeper) return 'blue';
    return null;
  })();

  // Default payer: whenever the active group (the selected one, or the fallback first group the modal
  // previews) has a FUNDED wallet and the bill isn't private, the wallet is the default payer — so the
  // field reads "Paid from: Wallet" the moment the modal opens (mobile seeds the wallet when balance >
  // 0), until the user picks someone else. The wallet covers up to its balance; a shortfall is caught
  // by the save guard, as on mobile. No wallet / private ⇒ the user pays (default "Paid by: You").
  const defaultPaidBy: PaidByResultEntry[] | null =
    effectiveWalletMinor !== null && effectiveWalletMinor > 0 && visibility !== 'private'
      ? [
          {
            // A wallet payer's id is the wallet ACCOUNT id (not the current user's) — the backend
            // rejects a wallet leg keyed by a user id ("payer's account was not found in this group").
            userId: splitGroup?.walletId ?? user?.id ?? '',
            type: 'wallet',
            // Wallet capacity is in the home/group currency, so cap against the distribution total.
            paidAmountMinor: Math.min(effectiveWalletMinor, distributionTotalMinor),
            splitEnabled: true,
          },
        ]
      : null;
  // What the field reflects: the user's modal override, else the default payer.
  const effectivePaidBy = paidByOverride ?? defaultPaidBy;

  // The total BOTH split modals display and distribute (entry space): when the wallet co-pays
  // alongside 2+ member payers, its contribution is deducted FIRST — the members only distribute
  // the remainder (a 150 bill with the wallet's 50 shows "Split Expenses of 100"), so
  // Σ displayed splits + wallet = bill and the wallet is never counted twice. This is a display
  // concept only — the wire `split_configs` carry full-total shares (the save path scales
  // entry → wire; the backend rejects deducted sums with "Payment mismatch" and nets the wallet
  // itself). Other payer shapes display the full distribution total, as before.
  const splitTotalMinor = Math.max(
    0,
    distributionTotalMinor -
      walletSplitDeductionMinor(
        (effectivePaidBy ?? []).map((p) => ({ type: p.type, paidMinor: p.paidAmountMinor })),
        distributionTotalMinor,
      ),
  );

  // Paid-by trigger label — reflects `effectivePaidBy`:
  //  • several payers      → "Multiple"
  //  • a lone wallet payer → "Wallet"
  //  • the current user    → "You"
  //  • one other member    → that member's name
  //  • nothing (no wallet) → "You" (the current user pays the full bill)
  const paidByButtonLabel = (() => {
    const payers = effectivePaidBy ?? [];
    if (payers.length === 0) return m.paidByYou;
    if (payers.length > 1) return m.paidByMultiple;
    const only = payers[0];
    if (!only || only.type === 'wallet') return m.paidByWallet;
    if (user?.id && only.userId === user.id) return m.paidByYou;
    return splitMembers.find((mem) => mem.id === only.userId)?.name ?? m.paidByYou;
  })();
  // The field label reads "Paid from" when the sole payer is the wallet, else the default "Paid by:".
  const payers = effectivePaidBy ?? [];
  const paidByFieldLabel =
    payers.length === 1 && payers[0]?.type === 'wallet' ? m.paidFrom : m.paidBy;

  // The "Paid by …" name shown in the Split modal — the ACTUAL payer (wallet / member / multiple),
  // not always the current user. Falls back to the current user's name when nobody's chosen yet.
  const youName = user?.name ?? m.paidByYou;
  const splitPayerName = (() => {
    if (payers.length === 0) return youName;
    if (payers.length > 1) return m.paidByMultiple;
    const only = payers[0];
    if (!only || only.type === 'wallet') return m.paidByWallet;
    if (user?.id && only.userId === user.id) return youName;
    return splitMembers.find((mem) => mem.id === only.userId)?.name ?? youName;
  })();

  const saveBill = useSaveBill();

  // Clear every field back to the create-mode defaults. Called after a successful save so a reopened
  // modal starts blank (the component stays mounted, so state would otherwise persist).
  const resetForm = () => {
    setVisibility('group');
    setGroupId('');
    setDate(new Date()); // back to today's date
    setRepeat(null);
    setAmount('');
    setDescription('');
    setNote('');
    setFormError(null);
    setFieldErrors({});
    setSplitOverride(null);
    setSplitFromAssignment(false);
    setPaidByOverride(null);
    setChainPayerIds(null);
    setCategory(undefined);
    setCategoryManual(false);
    setReceiptFile(null);
    setReceiptUrl('');
    setCurrencyId('');
    setBreakdownItems([]);
    setBreakdownFees([]);
    setCurrencySearch('');
    setCurrencyConversionPopupOpen(false);
    setSavePreferredCurrency(false);
    // Location was missing from this reset — a reopened create modal kept the previous bill's
    // (or auto-attached) location.
    setLocation(null);
    setLocationOpen(false);
  };

  // Reset the form whenever the modal closes (X / Cancel / Esc / overlay) — not just after a save —
  // so a reopened create modal starts blank. (Edit-mode instances are keyed by id and remount anyway.)
  const handleOpenChange = (next: boolean) => {
    if (!next) resetForm();
    onOpenChange(next);
  };

  // Save: amount + currency + description + category + date + visibility + note, plus paid-by /
  // split for group expenses (auto equal-split / current-user-pays unless overridden in the modals).
  // Breakdown / receipt-binary / location / conversion remain deferred.
  const handleSave = () => {
    setFormError(null);
    setFieldErrors({});
    const trimmedAmount = amount.trim();
    const trimmedDescription = description.trim();
    const hasAttachment = hasReceipt;

    // Parse the amount (only when text is present and not a bare "."), mirroring `validatedAmount()`.
    const amountTextPresent = trimmedAmount !== '' && trimmedAmount !== '.';
    let parsedMinor: number | null = null;
    if (amountTextPresent) {
      try {
        parsedMinor = toMinorUnit(trimmedAmount);
      } catch {
        parsedMinor = null;
      }
    }
    const hasValidAmount = parsedMinor !== null && parsedMinor > 0;
    const amountInvalidEntered = amountTextPresent && parsedMinor === null;

    // Guard 1 (§6): need a valid amount OR a description OR an attachment; an entered-but-unparseable
    // amount always fails with the "valid amount" message.
    const mandatoryValid = hasValidAmount || trimmedDescription !== '' || hasAttachment;
    if (!mandatoryValid || amountInvalidEntered) {
      if (amountInvalidEntered) {
        // A bad amount was typed → short message under the amount field.
        setFormError(null);
        setFieldErrors({ amount: m.validation.amountInvalid });
      } else {
        // Cross-field ("amount OR description") → the long message goes in the footer; both fields
        // just get the red border ('' = flag only, no inline text).
        setFormError(m.validation.amountOrDescription);
        setFieldErrors({ amount: '', description: '' });
      }
      return;
    }
    // Description is mandatory: an amount-only save used to reach the backend, which answered
    // with a success envelope without persisting the expense (green toast, nothing saved).
    if (trimmedDescription === '') {
      setFormError(null);
      setFieldErrors({ description: m.validation.descriptionRequired });
      return;
    }
    // A group is mandatory for a NEW bill — group-less saves used to slip through and land in
    // mobile's "Needs your attention" as no-group entries. EDIT mode (`initial`) stays exempt so
    // the Needs-Attention "Update Details" repair flow can still save fixes to a no-group bill.
    // Field-style error (red border + message under the select), same as amount/description.
    if (!groupId && !initial) {
      setFormError(null);
      setFieldErrors({ group: m.validation.selectGroup });
      return;
    }
    // Privacy requires a group (mobile privacy toggle guard) — still needed for EDIT mode.
    if (visibility === 'private' && !groupId) {
      setFormError(null);
      setFieldErrors({ group: m.validation.selectGroup });
      return;
    }
    // A view-only member can't add expenses to the selected group — same toast the group pick
    // showed, repeated here so a fully filled form still can't reach the backend. New bills only:
    // EDIT mode (`initial`) keeps the Needs-Attention repair flow untouched.
    if (!initial && groupId && user?.id) {
      const saveGroup = selectableGroups.find((g) => g.id === groupId);
      if (saveGroup && isViewOnlyMember(saveGroup, user.id)) {
        toast.error(t.common.viewOnlyPermission);
        return;
      }
    }
    // Currency list must be loaded + a currency selected (its `_id` is the `currency_id`).
    if (!effectiveCurrencyId) {
      setFormError(m.validation.currencyRequired);
      return;
    }
    // A RECURRING expense saves via the group-bill endpoint, which requires a description.
    if (repeat && trimmedDescription === '') {
      setFormError(null);
      setFieldErrors({ description: m.validation.descriptionRequiredRecurring });
      return;
    }

    const minorTotal = parsedMinor ?? 0;
    const isPrivate = visibility === 'private';

    // ---- Breakdown / itemization (Guard 4 + wire `items[]`) ----
    // Line items + additional charges become `items[]`; a negative charge (discount) carries a "−"
    // sign, so the itemized total is `Σ item.amount + Σ fee.signedAmount`. Guard 4: that total must
    // equal the receipt (bill) amount before we can save. R5: a PRIVATE expense has no shared
    // breakdown — it's dropped from the payload (and the guard is skipped) when private.
    const breakdownRows = isPrivate
      ? []
      : [
          ...breakdownItems.map((row) => ({ row, type: 'item' })),
          ...breakdownFees.map((row) => ({ row, type: 'charge' })),
        ];
    let breakdownWire: BreakdownWireItem[] | undefined;
    if (breakdownRows.length > 0) {
      let itemizedMinor = 0;
      breakdownWire = breakdownRows.map(({ row, type }) => {
        // A charge is signed by its ROLE (a Discount subtracts — `signedChargeAmount`), the same
        // rule the Itemization / Assign steps total by. Reading the sign off the typed string
        // counted a "100" Discount as +100 here, so this guard/wire disagreed with the modal.
        const signedMinor = Math.round(
          (type === 'charge'
            ? signedChargeAmount(row.name, row.price, row.sign)
            : Number.parseFloat(row.price) || 0) * 100,
        );
        itemizedMinor += signedMinor;
        return {
          name: row.name.trim(),
          amountMinor: money(Math.abs(signedMinor)),
          type,
          sign: signedMinor < 0 ? ('-' as const) : ('+' as const),
        };
      });
      // ±0.90 tolerance (mobile parity, same window as the Itemization modal's Save gate). The
      // breakdown save already syncs the amount to the itemized total, so this only trips when
      // the amount was hand-edited afterwards and drifted beyond the allowed slack.
      if (Math.abs(itemizedMinor - minorTotal) > 90) {
        setFormError(m.validation.breakdownTotalMismatch);
        return;
      }
    }

    // ---- Paid-by / Split (group expenses only) ----
    let paidBy: PaidByEntry[] | undefined;
    let splitConfigs: SplitEntry[] | undefined;
    // The ENTRY-space split total (what the Split modals displayed and distributed): the
    // distribution total less the wallet's contribution when it co-pays with 2+ members — set
    // below once the final paid-by legs are known. The WIRE always carries full-total shares.
    let splitEntryTargetMinor = distributionTotalMinor;

    if (groupId) {
      // Paid-by: a PRIVATE expense is yours alone — the backend rejects a shared payer set (wallet /
      // multiple members) with `private_expense_invalid`, so force a single self-payer for the full
      // bill amount, ignoring any stale override (the Paid-by field is disabled while private). This
      // mirrors the mobile private payload. Otherwise use the modal override / default payer
      // (wallet-seeded), else the current user pays all.
      if (isPrivate) {
        if (user?.id) {
          paidBy = [
            { userId: user.id, type: 'user', paidAmount: money(minorTotal), splitEnabled: true },
          ];
        }
      } else if (effectivePaidBy && effectivePaidBy.length > 0) {
        paidBy = effectivePaidBy.map((p) => ({
          userId: p.userId,
          type: p.type,
          paidAmount: money(p.paidAmountMinor),
          splitEnabled: p.splitEnabled,
          // The chained paid-by method + raw values ride along to the wire (mobile parity).
          ...(p.splitType !== undefined ? { splitType: p.splitType } : {}),
          ...(p.percentage !== undefined ? { percentage: p.percentage } : {}),
          ...(p.shares !== undefined ? { shares: p.shares } : {}),
          ...(p.amount !== undefined ? { amount: p.amount } : {}),
        }));
      } else if (user?.id) {
        paidBy = [
          {
            userId: user.id,
            type: 'user',
            paidAmount: money(distributionTotalMinor),
            splitEnabled: true,
          },
        ];
      }

      // The captured paid-by legs can go stale — the amount changed after the payers were picked
      // (the selection is kept, but its per-payer amounts aren't), or a payer change flipped the
      // distribution currency (wallet settles in HOME, a member pays in the SELECTED bill
      // currency). When the legs no longer sum to the current total, REBUILD them for it —
      // mirroring the Paid-by modal's rule: the wallet pays up to its balance, the remainder
      // splits equally among the member payers. Legs that already sum to the total (e.g. an
      // unchanged edit) are preserved. Runs BEFORE the split section so the entry-space target
      // below reads the FINAL wallet leg.
      if (!isPrivate && paidBy && paidBy.length > 0) {
        const isLoneWallet = paidBy.length === 1 && paidBy[0]?.type === 'wallet';
        const paidSum = paidBy.reduce((acc, p) => acc + p.paidAmount, 0);
        // A LONE wallet leg is rescaled to the new total too, same as a mixed payer set below —
        // but ONLY when the wallet's current usable capacity (its balance plus whatever it
        // already contributed to THIS expense, restored — see `effectiveWalletMinor`) can fully
        // cover it. Editing a 250-of-300 wallet-paid expense up to 300 used to fail here: the
        // stale captured 250 leg was left untouched (never rescaled to 300), so the "paid-by
        // must equal the total" guard below always rejected the save even though the wallet
        // could afford the new amount. A GENUINELY under-funded wallet still falls through
        // unrescaled, so the mismatch guard correctly still catches that case.
        if (
          isLoneWallet &&
          paidBy[0] &&
          Math.abs(paidSum - distributionTotalMinor) > 1 &&
          effectiveWalletMinor !== null &&
          effectiveWalletMinor >= distributionTotalMinor
        ) {
          paidBy = [{ ...paidBy[0], paidAmount: money(distributionTotalMinor) }];
        } else if (!isLoneWallet && Math.abs(paidSum - distributionTotalMinor) > 1) {
          const walletLeg = paidBy.find((p) => p.type === 'wallet');
          const memberLegs = paidBy.filter((p) => p.type !== 'wallet');
          const rebuilt: PaidByEntry[] = [];
          let remaining = distributionTotalMinor;
          if (walletLeg) {
            const walletPaid = Math.min(effectiveWalletMinor ?? 0, distributionTotalMinor);
            // A wallet that can contribute NOTHING (balance drained since the legs were captured —
            // e.g. updating an older expense after the wallet was emptied) is DROPPED rather than
            // sent as a 0.00 payment leg, which the backend rejects as a validation error. The
            // member payers then cover the whole total between them.
            if (walletPaid > 0) {
              rebuilt.push({ ...walletLeg, paidAmount: money(walletPaid) });
              remaining -= walletPaid;
            }
          }
          const shares = equalSplitMinor(
            Math.max(0, remaining),
            memberLegs.map((p) => p.userId),
          );
          // A member left with NOTHING to pay (the wallet now covers the whole, smaller total —
          // e.g. an edit that drops the amount below the balance) is no longer a payer: drop the
          // leg instead of sending a 0.00 payment, which the ledger then listed as a "Multiple"
          // payer at 0.0. The wallet alone pays, as if it had been picked on its own.
          memberLegs.forEach((p) => {
            const share = shares[p.userId] ?? money(0);
            if (share > 0) rebuilt.push({ ...p, paidAmount: share });
          });
          paidBy = rebuilt;
        }
      }

      // Defence in depth: a leg that pays NOTHING must never reach the wire — the backend rejects
      // zero-amount wallet payments, and a 0.00 member leg is listed as a phantom "Multiple" payer
      // — no matter how it got into the captured legs (a stale override, an edit seed, a drained
      // balance, the wallet covering everything). The other legs already cover the total. A LONE
      // 0 leg is kept so the paid-total guard below flags it instead of silently sending no
      // payers at all.
      if (paidBy && paidBy.length > 1) {
        const paying = paidBy.filter((p) => p.paidAmount > 0);
        if (paying.length > 0) paidBy = paying;
      }

      // A repeating bill can't be paid from the wallet (mobile §16.1). The Repeat field and the
      // Paid-by modal each block the other order of picking, but a saved recurring expense can
      // still reach here with a wallet leg (an edit seed, a stale override) — refuse the save
      // rather than let the backend reject it.
      if (repeat && paidBy?.some((p) => p.type === 'wallet')) {
        setFormError(m.validation.recurrenceNotAvailableWallet);
        return;
      }

      // What the Split modals actually displayed/distributed (entry space) — see `splitTotalMinor`.
      splitEntryTargetMinor = Math.max(
        0,
        distributionTotalMinor -
          walletSplitDeductionMinor(
            (paidBy ?? []).map((p) => ({ type: p.type, paidMinor: p.paidAmount })),
            distributionTotalMinor,
          ),
      );

      // PAID-BY and SPLIT are fully separate concerns: `splitOverride` only ever holds a split
      // saved from the Split FIELD's modal, an edit seed, or the Breakdown's item assignment —
      // the Paid-by chained popup writes the paid_by legs above and never this — so whatever is
      // here IS the real split config.
      const wireSplitOverride = splitOverride;

      // Split: omitted for private expenses; else the modal override, else an equal split.
      if (!isPrivate) {
        if (wireSplitOverride && wireSplitOverride.length > 0) {
          splitConfigs = wireSplitOverride.map((s) => ({
            userId: s.userId,
            expenseAmount: money(s.expenseAmountMinor),
            role: 'member',
            splitEnabled: s.splitEnabled,
            splitType: s.splitType,
            ...(s.amount !== undefined ? { amount: s.amount } : {}),
            ...(s.percentage !== undefined ? { percentage: s.percentage } : {}),
            ...(s.shares !== undefined ? { shares: s.shares } : {}),
          }));
          // The wire lists EVERY group member. A subset save only carries the participating
          // members — append the rest as split_enabled=false with a zero amount so the backend
          // gets an explicit flag per member instead of missing rows. Appended at the END:
          // serializers/labels that read the split type off the first entry keep seeing a real
          // one, and the rescale/guards below only sum ENABLED legs.
          const inSplit = new Set(wireSplitOverride.map((s) => s.userId));
          const fillerType = wireSplitOverride[0]?.splitType ?? 'Equal';
          splitMembers
            .filter((mem) => !inSplit.has(mem.id))
            .forEach((mem) =>
              splitConfigs?.push({
                userId: mem.id,
                expenseAmount: money(0),
                role: 'member',
                splitEnabled: false,
                splitType: fillerType,
              }),
            );
        } else {
          const shares = equalSplitMinor(
            distributionTotalMinor,
            splitMembers.map((mem) => mem.id),
          );
          splitConfigs = splitMembers.map((mem) => ({
            userId: mem.id,
            expenseAmount: shares[mem.id] ?? money(0),
            role: 'member',
            splitEnabled: true,
            splitType: 'Equal',
          }));
        }
      }

      // A SPLIT BY AMOUNT is entered as absolute figures — it must add up to the ENTRY total
      // itself (the modal's displayed total: the bill, less the wallet's contribution when it
      // co-pays with 2+ members), so a mismatch (e.g. after the amount changed) is BLOCKED rather
      // than silently rescaled. Other modes recompute from their ratio. The WIRE always carries
      // each member's share of the FULL distribution total — the backend requires the split legs
      // to sum to the expense amount ("Payment mismatch" otherwise) and nets the wallet's
      // contribution itself — so once the entry-space figures validate, every mode is scaled up
      // to the full total for the payload.
      const splitByAmountSave = splitConfigs?.some((s) => s.amount !== undefined);
      if (
        splitConfigs &&
        splitConfigs.length > 0 &&
        wireSplitOverride &&
        wireSplitOverride.length > 0
      ) {
        const enabled = splitConfigs.map((s) => s.splitEnabled);
        const enabledSum = splitConfigs
          .filter((s) => s.splitEnabled)
          .reduce((acc, s) => acc + s.expenseAmount, 0);
        if (
          splitByAmountSave &&
          Math.abs(enabledSum - splitEntryTargetMinor) > 1 &&
          // Already in wire space (e.g. edit-mode legs seeded straight from the backend) is fine.
          Math.abs(enabledSum - distributionTotalMinor) > 1
        ) {
          setFormError(m.validation.splitMismatch);
          return;
        }
        if (enabledSum > 0 && Math.abs(enabledSum - distributionTotalMinor) > 1) {
          const legs = convertPartsMinor(
            splitConfigs.map((s) => s.expenseAmount),
            distributionTotalMinor / enabledSum,
            distributionTotalMinor,
            enabled,
          );
          splitConfigs = splitConfigs.map((s, i) => {
            const rescaled = money(legs[i] ?? 0);
            // Split-by-amount entries carry the raw major `amount` (wire `value`); keep it in sync with
            // the rescaled minor amount so a later edit reopens with matching figures. Percentage /
            // shares are ratios and stay valid unchanged.
            return {
              ...s,
              expenseAmount: rescaled,
              ...(s.amount !== undefined ? { amount: Number((rescaled / 100).toFixed(2)) } : {}),
            };
          });
        }
      }

      // Guards 5/6b + 6: paid-by / enabled-split totals must equal the DISTRIBUTION total (±1) — the
      // converted home total when conversion is active, else the bill total. (The modal amounts are in
      // that same currency.)
      if (paidBy) {
        const sum = paidBy.reduce((acc, p) => acc + p.paidAmount, 0);
        if (Math.abs(sum - distributionTotalMinor) > 1) {
          setFormError(m.validation.paidByMismatch);
          return;
        }
      }
      if (splitConfigs && splitConfigs.length > 0) {
        const sum = splitConfigs
          .filter((s) => s.splitEnabled)
          .reduce((acc, s) => acc + s.expenseAmount, 0);
        if (Math.abs(sum - distributionTotalMinor) > 1) {
          setFormError(m.validation.splitMismatch);
          return;
        }
      }
    }

    // ---- Currency conversion (bill currency ≠ the group's home currency) ----
    // When active, attach the group-currency legs (group_paid_amount / group_expense_amount) + the
    // top-level exchange_rate*/selected_currency_* block, and run the conversion guards (§6 2/3/6a).
    let conversion: SaveBillConversion | undefined;
    // Only convert when there's an amount to convert — a description/attachment-only save (amount 0)
    // has nothing to convert, so the conversion guards ("too small", etc.) must not block it.
    if (conversionActive && minorTotal > 0) {
      // Guard 2: the exchange rate must be loaded before we can convert.
      if (!exchangeRate || exchangeRate.rate <= 0) {
        setFormError(m.validation.exchangeRateLoading);
        return;
      }
      const rate = exchangeRate.rate;
      // Guard 3: the converted (home) total must be greater than zero.
      if (convertedTotalMinor <= 0) {
        setFormError(m.validation.convertedTooSmall);
        return;
      }
      // Guard 6a: each split participant's converted share must be ≥ 1 minor unit ({sym}0.01).
      const enabledSplitCount = (splitConfigs ?? []).filter((s) => s.splitEnabled).length;
      if (enabledSplitCount > 0 && convertedTotalMinor / enabledSplitCount < 1) {
        setFormError(
          m.validation.convertedTooSmallToSplit
            .replace('{count}', String(enabledSplitCount))
            .replace('{symbol}', homeCurrencySymbol),
        );
        return;
      }
      if (useHomeDistribution) {
        // Wallet pays → the paid-by / split amounts are ALREADY in the home currency (the modals
        // distributed the converted total). So `group_*_amount` = those amounts, and the bill-currency
        // `*_amount` is home ÷ rate — the last eligible entry absorbs the rounding remainder vs the
        // bill total (§9).
        if (paidBy && paidBy.length > 0) {
          const billLegs = convertPartsMinor(
            paidBy.map((p) => p.paidAmount),
            1 / rate,
            minorTotal,
          );
          paidBy = paidBy.map((p, i) => ({
            ...p,
            paidAmount: money(billLegs[i] ?? 0),
            groupPaidAmount: money(p.paidAmount),
          }));
        }
        if (splitConfigs && splitConfigs.length > 0) {
          const enabled = splitConfigs.map((s) => s.splitEnabled);
          const billLegs = convertPartsMinor(
            splitConfigs.map((s) => s.expenseAmount),
            1 / rate,
            minorTotal,
            enabled,
          );
          splitConfigs = splitConfigs.map((s, i) => ({
            ...s,
            expenseAmount: money(billLegs[i] ?? 0),
            groupExpenseAmount: money(s.expenseAmount),
          }));
        }
      } else {
        // You / a member pays → the paid-by / split amounts are in the SELECTED bill currency, so the
        // wire `*_amount` stays as entered; the group-currency legs (`group_*_amount`) are bill × rate,
        // the last eligible entry absorbing the remainder vs the converted home total.
        if (paidBy && paidBy.length > 0) {
          const homeLegs = convertPartsMinor(
            paidBy.map((p) => p.paidAmount),
            rate,
            convertedTotalMinor,
          );
          paidBy = paidBy.map((p, i) => ({
            ...p,
            groupPaidAmount: money(homeLegs[i] ?? 0),
          }));
        }
        if (splitConfigs && splitConfigs.length > 0) {
          const enabled = splitConfigs.map((s) => s.splitEnabled);
          const homeLegs = convertPartsMinor(
            splitConfigs.map((s) => s.expenseAmount),
            rate,
            convertedTotalMinor,
            enabled,
          );
          splitConfigs = splitConfigs.map((s, i) => ({
            ...s,
            groupExpenseAmount: money(homeLegs[i] ?? 0),
          }));
        }
      }
      conversion = {
        rate,
        source: '',
        // Mirror the fetch: the rate is for today when the expense date is in the future.
        date: exchangeRate.date || fxDateYmd,
        convertedTotal: money(convertedTotalMinor),
        groupCurrencyId: homeCurrencyId,
      };
    }

    // ---- Recurrence (Repeat) ---- present only when the user set a repeat rule (§16). The Repeat
    // modal supports both end types — "never" and "On Date" (which sends `end_type: date` + the
    // chosen `end_date`); the early reminder maps to whole days before.
    const reminderDaysMap: Record<string, number> = {
      none: 0,
      sameDay: 0,
      oneDay: 1,
      twoDays: 2,
      oneWeek: 7,
    };
    const recurrence: SaveBillRecurrence | undefined = repeat
      ? {
          frequency: repeat.frequency,
          interval: repeat.every,
          startDate: repeat.date ?? null,
          endType: repeat.endRepeat === 'onDate' ? 'date' : 'never',
          endDate: repeat.endRepeat === 'onDate' ? repeat.endDate ?? null : null,
          reminderDaysBefore: reminderDaysMap[repeat.reminder] ?? 0,
        }
      : undefined;

    saveBill.mutate(
      {
        // Edit mode ⇒ send the id so the save updates the existing expense.
        ...(initial?.id ? { id: initial.id } : {}),
        // A committed group-ledger edit routes to the group-expense endpoint (PUT), not pending.
        ...(initial?.isGroupExpense ? { isGroupExpense: true } : {}),
        groupId: groupId || null,
        // Current user id — needed by the recurring group-expense endpoint (`user_id`).
        ...(user?.id ? { userId: user.id } : {}),
        currencyId: effectiveCurrencyId,
        amount: trimmedAmount,
        description,
        expenseTypeId: effectiveCategory ?? null,
        date: date ?? new Date(),
        isPrivate,
        note,
        // Keep an existing receipt (edit mode) so saving without changing it doesn't drop the file.
        ...(receiptUrl ? { receiptUrl } : {}),
        // The binary is uploaded separately after save (see useSaveBill); passing the File here
        // also drives `file_extension` + keeps `remove_file` false in the create multipart.
        ...(receiptFile ? { receiptFile } : {}),
        ...(breakdownWire ? { breakdownItems: breakdownWire } : {}),
        ...(paidBy ? { paidBy } : {}),
        ...(splitConfigs ? { splitConfigs } : {}),
        ...(initial?.storedSplitType ? { storedSplitType: initial.storedSplitType } : {}),
        ...(conversion ? { conversion } : {}),
        ...(recurrence ? { recurrence } : {}),
        ...(savePreferredCurrency ? { savePreferredCurrency: true } : {}),
        location,
      },
      {
        onSuccess: () => {
          resetForm(); // clear the form so a reopened modal starts blank
          onOpenChange(false);
        },
        // No onError: the global mutation-cache handler toasts the failure once — the backend's
        // reason is NOT mirrored inline in the popup (it duplicated the toast, e.g. the
        // deleted-user edit rejection).
      },
    );
  };

  return (
    <>
      <Dialog.Root open={open} onOpenChange={handleOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
          <Dialog.Content
            aria-describedby={undefined}
            className={cn(
              'fixed inset-0 z-50 flex flex-col overflow-hidden bg-surface-raised shadow-overlay focus:outline-none',
              'sm:inset-auto sm:left-1/2 sm:top-1/2 sm:max-h-[92vh] sm:w-[calc(100vw-2rem)] sm:max-w-275 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-xl',
            )}
          >
            <Dialog.Title className="sr-only">{m.title}</Dialog.Title>

            {/* Header bar */}
            <div className="flex items-center gap-4 bg-header-bar px-5 py-4">
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-2">
                <span className="text-lg font-semibold text-fg-strong">{m.title}</span>
                <div className="flex items-end gap-4">
                  {(() => {
                    const selectedGroup = selectableGroups.find((g) => g.id === groupId);
                    const selectedGroupLabel = selectedGroup
                      ? selectedGroup.name.length > 30
                        ? `${selectedGroup.name.slice(0, 30)}...`
                        : selectedGroup.name
                      : undefined;
                    return (
                      // Relative so the validation message hangs below the trigger without
                      // growing the header row (same trick as the amount field's error).
                      <div className="relative">
                        <Select
                          value={groupId}
                          onValueChange={(v) => {
                            // A view-only member can't add expenses to the picked group — REJECT
                            // the pick (toast + the field keeps its previous value; the Save guard
                            // repeats the same check as a backstop).
                            const picked = selectableGroups.find((g) => g.id === v);
                            if (picked && user?.id && isViewOnlyMember(picked, user.id)) {
                              toast.error(t.common.viewOnlyPermission);
                              return;
                            }
                            setGroupId(v);
                            // Picking a group resolves its validation error (same as amount/description).
                            setFormError(null);
                            setFieldErrors({});
                            setSplitOverride(null);
                            setSplitFromAssignment(false);
                            setPaidByOverride(null);
                            // Chained payer ids reference the OLD group's members — close/clear so a
                            // stale payer-scoped popup can't survive a group switch.
                            setChainPayerIds(null);
                            // `effectiveCurrencyId` prioritises a manual `currencyId` pick over the new
                            // group's own preferred currency — clear it so switching groups actually
                            // switches to that group's currency instead of keeping the old manual pick.
                            // The "Set as {currency}" confirmation was tied to that discarded pick too.
                            setCurrencyId('');
                            setSavePreferredCurrency(false);
                          }}
                        >
                          <SelectTrigger
                            aria-label={m.selectGroup}
                            aria-invalid={fieldErrors.group !== undefined}
                            className={cn(
                              'h-11 w-[266px] max-w-full rounded-md ring-1 ring-danger/50 [&>span]:truncate',
                              // Required-field error: strong red border, same as amount/description.
                              fieldErrors.group !== undefined && 'ring-2 ring-danger',
                            )}
                          >
                            <SelectValue placeholder={m.selectGroup}>
                              {selectedGroupLabel}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent
                            className={cn(
                              'max-w-[calc(100vw-2.5rem)] rounded-lg border border-border bg-surface-raised shadow-overlay',
                              // The 580px width fits the group rows' icon + name comfortably — with no
                              // rows, keep the popover to the trigger's own width instead (Select.tsx's
                              // base `w-(--radix-select-trigger-width)` viewport class then applies), so a
                              // one-line message doesn't sit inside an oversized, mismatched box.
                              selectableGroups.length > 0
                                ? 'w-[580px]'
                                : 'w-(--radix-select-trigger-width)',
                            )}
                            viewportClassName="p-0 w-full"
                            sideOffset={-48}
                          >
                            {selectableGroups.length === 0 ? (
                              // No groups on the account yet — a real message instead of the popover
                              // opening onto an empty list (which rendered as a stray horizontal line: a
                              // bordered box with no rows inside collapses to ~0 height).
                              <p className="px-6 py-[14px] text-sm text-muted">
                                {m.noGroupsAvailable}
                              </p>
                            ) : (
                              <div className="flex max-h-160 flex-col overflow-y-auto">
                                {selectableGroups.map((g) => {
                                  const Icon = TYPE_ICONS[g.type];
                                  return (
                                    <SelectPrimitive.Item
                                      key={g.id}
                                      value={g.id}
                                      className="relative flex cursor-pointer select-none items-center gap-4 border-b border-border-soft py-[14px] px-6 text-sm text-fg outline-none last:border-0 data-highlighted:bg-black/2"
                                    >
                                      <Icon className="size-[42px] shrink-0 rounded-full" />
                                      <span className="min-w-0 flex-1 text-sm font-medium text-fg-strong">
                                        <SelectPrimitive.ItemText>
                                          {g.name}
                                        </SelectPrimitive.ItemText>
                                      </span>
                                      {/* Green tick on the selected group. */}
                                      <SelectPrimitive.ItemIndicator className="ml-auto">
                                        <Check className="size-5 text-positive" />
                                      </SelectPrimitive.ItemIndicator>
                                    </SelectPrimitive.Item>
                                  );
                                })}
                              </div>
                            )}
                          </SelectContent>
                        </Select>
                        {fieldErrors.group ? (
                          <p
                            role="alert"
                            className="absolute left-0 top-full mt-0.5 truncate text-[11px] font-medium text-danger"
                          >
                            {fieldErrors.group}
                          </p>
                        ) : null}
                      </div>
                    );
                  })()}
                </div>
              </div>
              <Dialog.Close
                aria-label={m.close}
                className="flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-border-soft text-fg hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X className="size-4" />
              </Dialog.Close>
            </div>

            {/* Body */}
            <div className="min-h-0 flex-1 overflow-y-auto">
              <div className="flex flex-col gap-6 p-6">
                {/* Currency · Amount · Paid by · Split */}
                <div className="flex flex-wrap items-end gap-x-8 gap-y-4">
                  <div className="flex flex-1 items-end gap-2 sm:flex-none">
                    <div className="flex w-22.5 shrink-0 flex-col gap-1">
                      <FieldLabel>{m.currency}</FieldLabel>
                      <Select
                        value={effectiveCurrencyId}
                        onValueChange={(newId) => {
                          const picked = currencies.find((c) => c.id === newId);
                          // Gate currency conversion for free users before allowing a non-home pick.
                          if (picked && homeCurrencyCode && picked.code !== homeCurrencyCode) {
                            if (!planUsageManager.check(FreeLimitType.currencyConversion)) return;
                          }
                          previousCurrencyIdRef.current = currencyId;
                          setCurrencyId(newId);
                          // The "Set Currency preference" popup (and the save_preferred_currency it
                          // can set) is a NEW-bill-only nudge — offering to remember a currency the
                          // user hasn't used with this group before. EDIT mode (`initial`) still lets
                          // the currency change freely; it just never asks to save it as a future
                          // preference (mobile parity: the popup is create-only there too).
                          if (
                            picked &&
                            homeCurrencyCode &&
                            picked.code !== homeCurrencyCode &&
                            groupId &&
                            !initial
                          ) {
                            setCurrencyConversionPopupOpen(true);
                          }
                        }}
                        open={currencySelectOpen}
                        onOpenChange={(open) => {
                          setCurrencySelectOpen(open);
                          if (!open) setCurrencySearch('');
                        }}
                      >
                        <SelectTrigger aria-label={m.currency} className="h-[48px]">
                          {selectedCurrency ? (
                            <span className="text-sm font-semibold text-fg">
                              {selectedCurrency.symbol}
                            </span>
                          ) : (
                            <SelectValue placeholder={m.currency} />
                          )}
                        </SelectTrigger>
                        <SelectContent
                          className="w-[393px] max-w-[calc(100vw-2.5rem)] rounded-xl border border-border bg-surface-raised shadow-overlay"
                          viewportClassName="p-2 w-full flex flex-col gap-3"
                          sideOffset={-50}
                          onKeyDown={(e) => {
                            if (
                              (e.key.length === 1 || e.key === 'Backspace' || e.key === 'Delete') &&
                              !e.ctrlKey &&
                              !e.metaKey &&
                              !e.altKey
                            ) {
                              currencySearchInputRef.current?.focus();
                            }
                          }}
                        >
                          {/* Search field */}
                          <div
                            className="flex items-center justify-between rounded-lg border border-border-soft bg-surface-raised px-4 py-2"
                            onPointerDown={(e) => e.stopPropagation()}
                          >
                            <input
                              ref={currencySearchInputRef}
                              type="text"
                              placeholder="Search"
                              value={currencySearch}
                              onChange={(e) => setCurrencySearch(e.target.value)}
                              onKeyDown={(e) => {
                                if (
                                  e.key !== 'ArrowDown' &&
                                  e.key !== 'ArrowUp' &&
                                  e.key !== 'Enter'
                                ) {
                                  e.stopPropagation();
                                }
                              }}
                              className="w-full bg-transparent text-sm text-fg outline-none placeholder:text-border-strong"
                            />
                            <Search className="size-4 text-border-strong shrink-0" />
                          </div>

                          {/* Currency rows — inner scroll so the full list (200+) doesn't overflow. */}
                          <div className="flex max-h-[320px] flex-col overflow-y-auto">
                            {filteredCurrencies.length === 0 ? (
                              <div className="px-4 py-3 text-sm text-muted-soft text-center">
                                No currencies found
                              </div>
                            ) : (
                              filteredCurrencies.map((row, i) => (
                                <SelectPrimitive.Item
                                  key={row.id}
                                  value={row.id}
                                  className={cn(
                                    'relative flex cursor-pointer select-none items-center gap-3 px-2 py-3 outline-none data-highlighted:bg-black/2',
                                    i !== filteredCurrencies.length - 1
                                      ? 'border-b border-border-soft'
                                      : '',
                                  )}
                                >
                                  <CurrencyFlag
                                    flagImage={row.flagImage}
                                    flag={row.flag}
                                    iso2={row.iso2}
                                    className="shrink-0"
                                  />
                                  <span className="hidden">
                                    <SelectPrimitive.ItemText>
                                      {row.symbol}
                                    </SelectPrimitive.ItemText>
                                  </span>
                                  <span className="w-[38px] text-sm font-semibold text-fg">
                                    {row.code}
                                  </span>
                                  <span className="flex-1 text-sm text-fg">{row.name}</span>
                                  <SelectPrimitive.ItemIndicator>
                                    <Check className="size-5 text-success" strokeWidth={2.5} />
                                  </SelectPrimitive.ItemIndicator>
                                </SelectPrimitive.Item>
                              ))
                            )}
                          </div>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="relative flex min-w-0 flex-1 flex-col gap-1 sm:w-[260px] sm:flex-none">
                      <FieldLabel
                        hint={
                          convertedDisplay ? (
                            <span className="text-xs text-danger">
                              {m.exchangeLabel} {convertedDisplay}
                            </span>
                          ) : undefined
                        }
                      >
                        {m.amount}
                      </FieldLabel>
                      <Input
                        aria-label={m.amount}
                        inputMode="decimal"
                        value={amount}
                        aria-invalid={fieldErrors.amount !== undefined}
                        onChange={(e) => {
                          const nextAmount = sanitizeAmountInput(e.target.value);
                          let nextMinor = 0;
                          try {
                            nextMinor = toMinorUnit(nextAmount);
                          } catch {
                            nextMinor = 0;
                          }
                          setAmount(nextAmount);
                          setFormError(null);
                          setFieldErrors({});
                          // The Paid-by AND Split SELECTIONS/modes are kept when the amount changes (so
                          // "Multiple" / "Split by amount" don't snap back to Wallet / Evenly). A split
                          // BY AMOUNT is left as ENTERED — it must add up to the total, so a changed
                          // total is validated (blocked) on save rather than silently rescaled. Other
                          // modes (evenly / shares / percentage) recompute from their ratio, so rescale
                          // their minor amounts here; paid-by amounts are rebuilt below.
                          const splitByAmount = splitOverride?.some(
                            (sp) => sp.amount !== undefined,
                          );
                          if (
                            splitOverride &&
                            !splitByAmount &&
                            totalMinor > 0 &&
                            nextMinor > 0 &&
                            nextMinor !== totalMinor
                          ) {
                            const enabled = splitOverride.map((sp) => sp.splitEnabled);
                            // `splitOverride` holds ENTRY-space figures (what the Split modals
                            // displayed), so rescale to the NEW entry total: the new distribution
                            // total less the wallet's contribution for it when the wallet co-pays
                            // with 2+ members — mirroring the paid-by rebuild just below. Ratio
                            // from the old enabled sum keeps proportions when the old legs were
                            // themselves deducted.
                            const splitWalletInvolved =
                              paidByOverride?.some((pp) => pp.type === 'wallet') ?? false;
                            const nextDistTotal =
                              splitWalletInvolved && conversionRate > 0
                                ? Math.round(nextMinor * conversionRate)
                                : nextMinor;
                            const nextSplitTarget = Math.max(
                              0,
                              nextDistTotal -
                                walletSplitDeductionMinor(
                                  (paidByOverride ?? []).map((pp) => ({
                                    type: pp.type,
                                    paidMinor:
                                      pp.type === 'wallet'
                                        ? Math.min(effectiveWalletMinor ?? 0, nextDistTotal)
                                        : 0,
                                  })),
                                  nextDistTotal,
                                ),
                            );
                            const oldEnabledSum = splitOverride
                              .filter((sp) => sp.splitEnabled)
                              .reduce((acc, sp) => acc + sp.expenseAmountMinor, 0);
                            const legs = convertPartsMinor(
                              splitOverride.map((sp) => sp.expenseAmountMinor),
                              oldEnabledSum > 0 ? nextSplitTarget / oldEnabledSum : 1,
                              nextSplitTarget,
                              enabled,
                            );
                            setSplitOverride(
                              splitOverride.map((sp, i) => {
                                const rescaled = legs[i] ?? 0;
                                return {
                                  ...sp,
                                  expenseAmountMinor: rescaled,
                                  ...(sp.amount !== undefined
                                    ? { amount: Number((rescaled / 100).toFixed(2)) }
                                    : {}),
                                };
                              }),
                            );
                          }
                          // Paid-by amounts are absolute too — rebuild them for the new total (wallet
                          // caps at its balance, members split the remainder equally) so "Multiple"
                          // reconciles instead of tripping the paid-by total guard. A lone wallet is
                          // left as-is (its shortfall is flagged separately).
                          if (paidByOverride && nextMinor > 0) {
                            const loneWallet =
                              paidByOverride.length === 1 && paidByOverride[0]?.type === 'wallet';
                            if (!loneWallet && paidByOverride.length > 0) {
                              const walletInvolved = paidByOverride.some(
                                (pp) => pp.type === 'wallet',
                              );
                              const newTotal =
                                walletInvolved && conversionRate > 0
                                  ? Math.round(nextMinor * conversionRate)
                                  : nextMinor;
                              const walletLeg = paidByOverride.find((pp) => pp.type === 'wallet');
                              const memberLegs = paidByOverride.filter(
                                (pp) => pp.type !== 'wallet',
                              );
                              const rebuilt: PaidByResultEntry[] = [];
                              let remaining = newTotal;
                              if (walletLeg) {
                                const walletPaid = Math.min(effectiveWalletMinor ?? 0, newTotal);
                                // A drained wallet (0 contribution) is dropped, not kept as a
                                // 0.00 leg — mirrors the save-path rebuild; the backend rejects
                                // zero-amount wallet payment legs.
                                if (walletPaid > 0) {
                                  rebuilt.push({ ...walletLeg, paidAmountMinor: walletPaid });
                                  remaining -= walletPaid;
                                }
                              }
                              const sh = equalSplitMinor(
                                Math.max(0, remaining),
                                memberLegs.map((pp) => pp.userId),
                              );
                              // A member left with nothing to pay (the wallet now covers the
                              // whole, smaller total) is dropped — the field falls back to
                              // "Wallet" instead of a "Multiple" that includes 0.00 payers
                              // (mirrors the save-path rebuild).
                              memberLegs.forEach((pp) => {
                                const share = Number(sh[pp.userId] ?? 0);
                                if (share > 0) rebuilt.push({ ...pp, paidAmountMinor: share });
                              });
                              setPaidByOverride(rebuilt);
                            }
                          }
                        }}
                        placeholder={m.amountPlaceholder}
                        className="rounded-md"
                      />
                      {/* Absolute so the message hangs below the input without growing the row (which
                          would knock the currency dropdown + Paid-by/Split out of alignment). Wallet
                          balance uses the same trick, under the same input — a field error takes
                          priority over it (more urgent, and the two would otherwise overlap). */}
                      {fieldErrors.amount ? (
                        <p
                          role="alert"
                          className="absolute left-0 top-full mt-0.5 truncate text-[11px] font-medium text-danger"
                        >
                          {fieldErrors.amount}
                        </p>
                      ) : hasGroupWallet &&
                        visibility !== 'private' &&
                        groupWalletMinor !== null ? (
                        <p className="absolute left-0 top-full mt-0.5 truncate text-[11px] text-muted">
                          {m.walletBalance} {formatMoney(money(groupWalletMinor), homeCurrencyCode)}
                          {walletBalanceConvertedMinor !== null ? (
                            <>
                              {' '}
                              ({formatMoney(money(walletBalanceConvertedMinor), billCurrencyCode)})
                            </>
                          ) : null}
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
                    {/* Only the box triggers Paid-by / Split (or its validation) — the "Paid from:" /
                        "Split:" label text is plain, non-clickable. */}
                    <div className="flex items-center gap-3 text-base font-medium text-fg">
                      <span>{paidByFieldLabel}</span>
                      <button
                        type="button"
                        aria-disabled={paidByDisabled}
                        onClick={() => {
                          // Private ⇒ only the payer is involved — the button is inert. Otherwise the
                          // shared guards (amount / group) run before opening the Paid-by modal; once
                          // those pass, mobile parity: a single-member group with no wallet has no one
                          // to pick as payer at all, so surface that instead of opening an empty picker.
                          if (isPrivateExpense) return;
                          requirePaidBySplit(() => {
                            if (!hasPayerOption) {
                              toast.error(m.validation.noPayerAvailable);
                              return;
                            }
                            setPaidByOpen(true);
                          })();
                        }}
                        // Wallet-payer glow: red when the bill exceeds the wallet, blue when the user
                        // isn't the book-keeper (see `paidByGlow`) — darker border + gradient halo.
                        style={
                          paidByGlow
                            ? {
                                boxShadow: PAID_BY_GLOW[paidByGlow].shadow,
                                borderColor: PAID_BY_GLOW[paidByGlow].border,
                              }
                            : undefined
                        }
                        className={cn(
                          'flex h-[50px] w-[147px] items-center justify-between gap-1 rounded-md border border-[#dad9db] bg-accent-soft px-3 font-semibold text-fg transition-shadow duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                          paidByDisabled && 'opacity-40',
                        )}
                      >
                        <span className="min-w-0 truncate text-left">{paidByButtonLabel}</span>
                        <ChevronDown className="size-4 shrink-0 opacity-60" aria-hidden />
                      </button>
                    </div>
                    <div className="flex items-center gap-3 text-base font-medium text-fg">
                      <span>{m.split}</span>
                      <button
                        type="button"
                        aria-disabled={splitDisabled}
                        onClick={() => {
                          // Private / single-member ⇒ nothing to split — the button is inert. Otherwise
                          // the shared guards (amount / group) run before opening the Split modal.
                          if (visibility === 'private' || singleMemberGroup) return;
                          requirePaidBySplit(() => setSplitOpen(true))();
                        }}
                        className={cn(
                          'flex h-[50px] w-[147px] items-center justify-between gap-1 rounded-md border border-[#dad9db] bg-accent-soft px-3 font-semibold text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                          splitDisabled && 'opacity-40',
                        )}
                      >
                        <span className="min-w-0 truncate text-left">{splitButtonLabel}</span>
                        <ChevronDown className="size-4 shrink-0 opacity-60" aria-hidden />
                      </button>
                    </div>
                  </div>
                </div>

                {/* What was this for? */}
                <div className="relative flex flex-col gap-1">
                  <FieldLabel>{m.whatFor}</FieldLabel>
                  <div className="flex items-stretch gap-2">
                    <Input
                      aria-label={m.whatFor}
                      placeholder={m.descriptionPlaceholder}
                      value={description}
                      maxLength={150}
                      aria-invalid={fieldErrors.description !== undefined}
                      onChange={(e) => {
                        setDescription(e.target.value.slice(0, 150));
                        setFormError(null);
                        setFieldErrors({});
                      }}
                      className="flex-1 rounded-md"
                    />
                    <button
                      type="button"
                      aria-label={t.newBill.categoryModal.title}
                      title={selectedCategory?.name}
                      onClick={() => setCategoryOpen(true)}
                      className="flex h-[50px] shrink-0 cursor-pointer items-center gap-1.5 rounded-md border p-0.5 overflow-hidden border-border-soft text-info focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring hover:bg-surface transition-colors"
                    >
                      {/* Show the selected category's icon; fall back to the generic info glyph. */}
                      {selectedCategory?.image ? (
                        <img
                          src={selectedCategory.image}
                          alt=""
                          className="size-11 object-contain rounded-[4px]"
                          style={
                            selectedCategory.color
                              ? { backgroundColor: selectedCategory.color }
                              : undefined
                          }
                        />
                      ) : (
                        <span className="bg-[#CCE3FF] rounded-[4px]">
                          <OtherExpenseIcon className="size-11" />
                        </span>
                      )}
                      <ChevronDown className="size-4 text-fg" />
                    </button>
                  </div>
                  {fieldErrors.description ? (
                    <p
                      role="alert"
                      className="absolute left-0 top-full mt-0.5 truncate text-[11px] font-medium text-danger"
                    >
                      {fieldErrors.description}
                    </p>
                  ) : null}
                </div>

                {/* Date · Attach receipt · Add Breakdown */}
                <div className="flex flex-wrap gap-x-8 gap-y-4">
                  <div className="flex min-w-[240px] flex-1 flex-col gap-1">
                    <FieldLabel
                      hint={
                        repeat ? (
                          <span className="text-xs text-danger">{m.recurrence}</span>
                        ) : undefined
                      }
                    >
                      {m.date}
                    </FieldLabel>
                    <Popover.Root open={dateOpen} onOpenChange={setDateOpen}>
                      <Popover.Trigger asChild>
                        <button
                          type="button"
                          aria-label={m.date}
                          className="flex h-[50px] w-full items-center justify-between gap-2 rounded-md border border-border-soft bg-surface-raised px-4 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <span className={date ? 'text-fg' : 'text-fg/50'}>
                            {date ? formatDate(date) : m.date}
                          </span>
                          <Calendar className="size-[18px] text-muted-soft" />
                        </button>
                      </Popover.Trigger>
                      <Popover.Portal>
                        <Popover.Content
                          align="start"
                          side="bottom"
                          sideOffset={-159}
                          avoidCollisions={false}
                          className="z-55 rounded-xl border border-border bg-surface-raised p-3 shadow-overlay w-90"
                        >
                          <div className="border rounded-lg border-divider pt-3">
                            <CalendarView
                              mode="single"
                              fixedWeeks
                              className="w-full"
                              selected={date}
                              onSelect={setDate}
                              // A future date isn't valid for an expense that hasn't happened yet, but
                              // any PAST date must stay pickable (logging an expense from last week,
                              // say) — only block days after today. Don't lock month navigation
                              // backward either; cap it going forward at the current month, since every
                              // day in a future month would be disabled anyway.
                              disabled={{ after: calendarToday }}
                              endMonth={calendarToday}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setDate(calendarToday);
                              setDateOpen(false);
                            }}
                            className="mt-3 flex h-11 w-full cursor-pointer items-center justify-center rounded-xl bg-[#EBEBEB] text-sm font-medium text-[#161616] hover:bg-[#E0E0E0] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            {t.recordPayment.today}
                          </button>
                          <div className="mt-3 flex items-center justify-between gap-2">
                            {/* Only meaningful once a recurrence is actually set — was showing
                                unconditionally even for a plain one-time expense. */}
                            {repeat ? (
                              <button
                                type="button"
                                onClick={() => setRepeat(null)}
                                className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-danger/30 bg-danger/10 px-3 py-1.5 text-sm font-medium text-danger hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              >
                                <X className="size-4" aria-hidden /> {m.repeat.stopRepeat}
                              </button>
                            ) : null}
                            <button
                              type="button"
                              onClick={() => {
                                // Recurrence isn't available when the wallet is a payer (§16.1).
                                if (payers.some((p) => p.type === 'wallet')) {
                                  toast.error(m.validation.recurrenceNotAvailableWallet);
                                  return;
                                }
                                setRepeatOpen(true);
                              }}
                              className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-border-soft bg-surface-raised px-3 py-1.5 text-sm font-medium text-fg hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              <RepeatIcon2 className="size-4" aria-hidden /> {m.repeat.label}{' '}
                              {repeatLabel}
                            </button>
                          </div>
                        </Popover.Content>
                      </Popover.Portal>
                    </Popover.Root>
                  </div>

                  <div className="flex min-w-[240px] flex-1 flex-col gap-1">
                    <FieldLabel>{m.attachReceipt}</FieldLabel>
                    {hasReceipt ? (
                      <button
                        type="button"
                        onClick={() => setReceiptModalOpen(true)}
                        className="flex h-[50px] cursor-pointer items-center gap-2.5 rounded-md border border-success-border bg-success-soft px-4 text-left text-base text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring hover:bg-success-soft/80 transition-colors"
                      >
                        <span className="flex-1 truncate">{m.viewAttachment}</span>
                        <ReceiptIcon className="size-5 shrink-0 text-success-border" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          if (!planUsageManager.check(FreeLimitType.attachmentWeeklyLimit)) return;
                          setReceiptModalOpen(true);
                        }}
                        className="flex h-[50px] cursor-pointer items-center gap-2.5 rounded-md border border-border-soft bg-surface-raised px-4 text-left text-base text-[#008BFF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring hover:bg-surface transition-colors"
                      >
                        <span className="flex-1 truncate">{m.browseReceipt}</span>
                        <CloudUpload className="size-5 shrink-0 text-muted-soft" />
                      </button>
                    )}
                  </div>

                  <div className="flex min-w-[240px] flex-1 flex-col gap-1">
                    <FieldLabel>{m.addBreakdown}</FieldLabel>
                    {breakdownItems.length > 0 || breakdownFees.length > 0 ? (
                      <button
                        type="button"
                        aria-disabled={breakdownDisabled}
                        onClick={openBreakdown}
                        className={cn(
                          'flex h-[50px] cursor-pointer items-center gap-2.5 rounded-md border border-success-border bg-success-soft px-4 text-left text-base text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring hover:bg-success-soft/80 transition-colors',
                          breakdownDisabled && 'opacity-40',
                        )}
                      >
                        <span className="flex-1 truncate">{m.viewBreakdown}</span>
                        <BreakdownIcon className="size-6 shrink-0 text-success-border" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        aria-disabled={breakdownDisabled}
                        onClick={openBreakdown}
                        className={cn(
                          'flex h-[50px] cursor-pointer items-center gap-2.5 rounded-md border border-border-soft bg-surface-raised px-4 text-left text-base text-info focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring hover:bg-surface transition-colors',
                          breakdownDisabled && 'opacity-40',
                        )}
                      >
                        <span className="flex-1 truncate text-[#008BFF]">
                          {m.clickAddBreakdown}
                        </span>
                        <BreakdownIcon className="size-6 shrink-0 text-info" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Note */}
                <div className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium text-fg">{m.note}</span>
                  <textarea
                    aria-label={m.note}
                    placeholder={m.notePlaceholder}
                    value={note}
                    maxLength={200}
                    onChange={(e) => setNote(e.target.value.slice(0, 200))}
                    className="h-30 w-full resize-none rounded-xl border border-border-soft bg-surface-raised p-3 text-sm text-fg placeholder:text-muted-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>

                {/* Visibility + location */}
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <span className="text-base font-medium text-fg">{m.whoCanSee}</span>
                    <div className="flex rounded-xl border border-border-soft">
                      <button
                        type="button"
                        onClick={() => setVisibility('group')}
                        className={cn(
                          'flex cursor-pointer items-center gap-2 rounded-xl px-4 py-3 text-sm',
                          visibility === 'group'
                            ? 'bg-accent-soft border border-accent-soft font-semibold text-fg'
                            : 'text-muted',
                        )}
                      >
                        <Eye className="size-[18px]" /> {m.visibleToGroup}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setVisibility('private');
                          // A private expense is yours alone — it has no shared breakdown (dropped at
                          // save), so REMOVE it here too and the (now-disabled) trigger resets to its
                          // empty "Click to add breakdown" state. Paid-by/Split are disabled below.
                          setBreakdownItems([]);
                          setBreakdownFees([]);
                          setBreakdownModalOpen(false);
                        }}
                        className={cn(
                          'flex cursor-pointer items-center gap-2 rounded-xl px-4 py-3 text-sm',
                          visibility === 'private'
                            ? 'border border-danger-strong bg-danger-strong/10 font-semibold text-danger-strong'
                            : 'text-muted',
                        )}
                      >
                        <EyeOff className="size-[18px]" /> {m.privateToYou}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <span className="text-base font-medium text-fg">{m.addLocation}</span>
                    {location ? (
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => setLocationOpen(true)}
                          className="flex h-11 cursor-pointer items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-4 text-sm font-semibold text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring hover:bg-primary/10 transition-colors"
                        >
                          <LocationIcon className="size-4 shrink-0 text-primary" />
                          {m.attachLocation}
                        </button>
                        <button
                          type="button"
                          onClick={() => setLocation(null)}
                          className="flex size-9 cursor-pointer items-center justify-center rounded-full bg-border-soft text-muted hover:text-fg hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
                          aria-label={m.locationModal.removeLocation}
                        >
                          <X className="size-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setLocationOpen(true)}
                        className="flex h-11 cursor-pointer items-center gap-1.5 rounded-full border border-border-soft px-4 text-sm text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring hover:bg-surface-raised transition-colors"
                      >
                        <LocationIcon className="size-4 shrink-0 opacity-70" /> {m.attachLocation}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex flex-col gap-2 border-t border-border px-5 py-3">
              {formError ? (
                <p role="alert" className="text-[11px] font-medium text-danger">
                  {formError}
                </p>
              ) : null}
              <div className="flex gap-2">
                <Button onClick={handleSave} disabled={saveBill.isPending}>
                  {saveBill.isPending ? m.saving : m.save}
                </Button>
                <Dialog.Close asChild>
                  <Button variant="secondary">{m.cancel}</Button>
                </Dialog.Close>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      {/* "Set Currency preference" popup — Figma design §9. Shown when the user picks a
          non-home currency while a group is already selected so they can opt-in to saving it
          as their preferred entry currency for that group. No-op when no group is set. */}
      <Dialog.Root
        open={currencyConversionPopupOpen}
        onOpenChange={(o) => {
          if (!o) {
            // Dismissed via X/overlay/Escape (not an explicit "Set as" / "Not Now" choice, which set
            // `currencyConversionPopupOpen` themselves and never reach here) — undo the currency pick
            // that opened this popup instead of silently keeping it.
            setCurrencyId(previousCurrencyIdRef.current);
            setCurrencyConversionPopupOpen(false);
          }
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-60 bg-black/40" />
          <Dialog.Content
            aria-describedby="set-currency-pref-desc"
            className="fixed left-1/2 top-1/2 z-60 w-[90vw] max-w-[754px] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl bg-surface-raised shadow-overlay focus:outline-none"
          >
            {/* Header — h:56, py:8, px:20, gap:16, border-bottom */}
            <div className="flex h-14 items-center justify-between gap-4 border-b border-[#CACACA] bg-header-bar px-5 py-2">
              <Dialog.Title className="w-[214px] h-[22px] font-sans text-[18px] font-semibold leading-none tracking-normal text-[#000000] opacity-100">
                {m.setCurrencyPreference.title}
              </Dialog.Title>
              <Dialog.Close
                aria-label={m.close}
                className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-border-soft text-fg hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X className="size-4" />
              </Dialog.Close>
            </div>

            {/* Body — py:16, px:20, gap:8, border-bottom */}
            <div className="flex flex-col gap-2 border-b border-[#CACACA] px-5 py-4 text-base leading-relaxed text-muted">
              <p id="set-currency-pref-desc">
                {m.setCurrencyPreference.body
                  .replace('{currencyCode}', billCurrencyCode)
                  .replace('{currencySymbol}', selectedCurrency?.symbol ?? '')
                  .replace(
                    '{groupName}',
                    selectableGroups.find((g) => g.id === groupId)?.name ?? '',
                  )}
              </p>
              <p>{m.setCurrencyPreference.note}</p>
            </div>

            {/* Footer — h:64, py:12, px:20, gap:10, border-top #CACACA */}
            <div className="flex h-16 items-center gap-[10px] border-t border-[#CACACA] px-5 py-3">
              <Button
                onClick={() => {
                  setSavePreferredCurrency(true);
                  setCurrencyConversionPopupOpen(false);
                }}
              >
                {m.setCurrencyPreference.setAs.replace('{currencyCode}', billCurrencyCode)}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setSavePreferredCurrency(false);
                  setCurrencyConversionPopupOpen(false);
                }}
              >
                {m.setCurrencyPreference.notNow}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      <RepeatModal
        open={repeatOpen}
        onOpenChange={setRepeatOpen}
        value={repeat ?? undefined}
        onSave={(cfg) => {
          setRepeat(cfg);
          // The recurrence start date becomes the expense date (mobile §16.2) — so the Date field
          // reflects e.g. "02 Jul 2026" once a repeat rule with that start is set.
          if (cfg.date) setDate(cfg.date);
        }}
      />
      <SplitModal
        open={splitOpen}
        onOpenChange={setSplitOpen}
        // The Split FIELD's modal: always the WHOLE group (default 'evenly' participants) — the
        // Paid-by selection never reaches it (that flow has its own `PaidBySplitModal` below), so
        // "Evenly" genuinely means every group member. A saved split reopens exactly as saved.
        members={splitMembers}
        // Split in the HOME currency (converted total + home symbol) when conversion is active,
        // less the wallet's contribution when it co-pays with 2+ members (see `splitTotalMinor`).
        totalMinor={splitTotalMinor}
        currency={distributionCurrencyCode}
        payerName={splitPayerName}
        initial={splitOverride ?? undefined}
        onSave={(entries) => {
          setSplitOverride(entries);
          // A split configured by hand supersedes any breakdown-assignment-derived one.
          setSplitFromAssignment(false);
        }}
      />
      {/* The SEPARATE Split popup chained from a multi-payer Paid-by save — payer-scoped
          ("selected members": only the payers listed, the remainder split among them) until the
          user picks a split-by tab, which widens to the whole group. Its own component + open
          state so the two split flows can never show conflicting values. */}
      <PaidBySplitModal
        open={chainPayerIds !== null}
        onOpenChange={(o) => {
          if (!o) {
            setChainPayerIds(null);
            // Closed WITHOUT saving (Save clears the ref first) → undo the paid-by pick that
            // opened it, so the field falls back to the prior payer (e.g. the default wallet)
            // instead of staying on "Multiple".
            if (splitChainPendingRef.current) {
              setPaidByOverride(paidByBeforeChainRef.current);
              splitChainPendingRef.current = false;
              paidByBeforeChainRef.current = null;
            }
          }
        }}
        members={splitMembers}
        payerIds={chainPayerIds ?? []}
        totalMinor={splitTotalMinor}
        currency={distributionCurrencyCode}
        payerName={splitPayerName}
        onSave={(entries) => {
          // PAID-BY ONLY: this chained popup answers "how did the PAYERS split the payment" —
          // its participants are always exactly the payers (`selectedMembers` mode). Each
          // payer's PAID amount becomes their configured share, and the METHOD + raw value
          // ('Percentage' + each percentage, etc.) ride along to the wire payment legs. It
          // NEVER touches `splitOverride`: the expense's Split (field label, its modal, and the
          // wire split_configs) is a fully separate concern owned by the Split field alone.
          // The wallet's co-pay leg is never modified (the popup's total already excludes it).
          const paidById = new Map(entries.filter((e) => e.splitEnabled).map((e) => [e.userId, e]));
          // A payer UNCHECKED in the popup was removed from the payment — drop their leg entirely
          // (keeping it with the stale equal share would desync the paid total and trip the
          // equal-split rebuild on save, resurrecting the removed payer).
          const removedIds = new Set(entries.filter((e) => !e.splitEnabled).map((e) => e.userId));
          setPaidByOverride((current) =>
            (current ?? [])
              .filter((p) => p.type === 'wallet' || !removedIds.has(p.userId))
              .map((p) => {
                const entry = p.type !== 'wallet' ? paidById.get(p.userId) : undefined;
                if (!entry) return p;
                return {
                  ...p,
                  paidAmountMinor: entry.expenseAmountMinor,
                  splitType: entry.splitType,
                  ...(entry.percentage !== undefined ? { percentage: entry.percentage } : {}),
                  ...(entry.shares !== undefined ? { shares: entry.shares } : {}),
                  ...(entry.amount !== undefined ? { amount: entry.amount } : {}),
                };
              }),
          );
          // The chained popup was confirmed — keep the paid-by pick that opened it.
          splitChainPendingRef.current = false;
          paidByBeforeChainRef.current = null;
        }}
      />
      <PaidByModal
        open={paidByOpen}
        onOpenChange={setPaidByOpen}
        members={splitMembers}
        totalMinor={distributionTotalMinor}
        currency={distributionCurrencyCode}
        // The wallet's usable capacity — the current balance PLUS this expense's already-recorded
        // wallet contribution in edit mode (see `effectiveWalletMinor`).
        walletBalanceMinor={effectiveWalletMinor}
        walletId={splitGroup?.walletId ?? ''}
        // A repeating bill can't be paid from the wallet (mobile §16.1) — the modal blocks the row
        // (the Repeat field guards the reverse order: no repeat while the wallet pays).
        walletUnavailableForRecurring={repeat !== null}
        // Pre-check the current payer(s) — the default wallet, or whoever's already picked — so the
        // multi-select checkboxes open reflecting the current state instead of all-empty.
        initialPayers={effectivePaidBy ?? []}
        transactionTotalMinor={totalMinor}
        transactionCurrency={selectedCurrency?.code}
        onSave={(entries) => {
          // Chain into `PaidBySplitModal` only when 2+ MEMBERS pay (with or without the wallet) —
          // that's when the bill needs splitting among them. A single member (even alongside the
          // wallet), a lone wallet, or one payer saves as usual, no chained popup. The currency
          // follows the payer (wallet involved ⇒ home, else the bill currency). Remember the
          // prior payer first so cancelling that chained Split reverts to it.
          setPaidByOverride(entries);
          const memberIds = entries.filter((e) => e.type !== 'wallet').map((e) => e.userId);
          if (memberIds.length > 1) {
            paidByBeforeChainRef.current = paidByOverride;
            splitChainPendingRef.current = true;
            setChainPayerIds(memberIds);
          }
        }}
      />
      <CategoryModal
        open={categoryOpen}
        onOpenChange={setCategoryOpen}
        value={effectiveCategory}
        onSelect={(id) => {
          setCategory(id);
          setCategoryManual(true); // a hand-picked category suppresses description auto-classify
        }}
      />
      <ReceiptUploadModal
        key={`receipt-${receiptModalOpen ? 'open' : 'closed'}`}
        open={receiptModalOpen}
        onOpenChange={setReceiptModalOpen}
        // Show a newly-picked file, else the fetched EXISTING attachment (edit mode, API 5) — the
        // fetch itself only starts once this modal is open (`attachmentEnabled`), so on first open it
        // hasn't resolved yet; `isLoadingExisting` tells the modal to show a loading state instead of
        // the empty drop-zone for that gap, and it re-syncs `currentFile` once the fetch lands.
        currentFile={receiptFile ?? fetchedReceiptFile}
        isLoadingExisting={attachmentEnabled && attachmentQuery.isLoading}
        onUpload={(file) => {
          // A new file (or a removal) supersedes any existing receipt URL from edit mode.
          setReceiptFile(file);
          setReceiptUrl('');
        }}
      />
      <BreakdownModal
        key={`breakdown-${breakdownModalOpen ? 'open' : 'closed'}`}
        open={breakdownModalOpen}
        onOpenChange={setBreakdownModalOpen}
        initialItems={breakdownItems}
        initialFeesAndTaxes={breakdownFees}
        currencySymbol={selectedCurrency?.symbol ?? '$'}
        receiptAmount={amount}
        members={splitMembers}
        onSave={(newItems, newFees, assignedShares) => {
          setBreakdownItems(newItems);
          setBreakdownFees(newFees);
          // The expense amount reflects the breakdown's GRAND TOTAL (line items + additional charges).
          // In the Itemization step items always balance the receipt, so this leaves the amount as-is
          // there; a charge added in the "Assign each item" step grows the total, so the amount grows
          // to match (e.g. item 3 + tip 2 + tip 2 ⇒ 7). Loop-safe: on reopen the receipt = amount, so
          // items + charges balance it again.
          // Charges are signed by role — a Discount SUBTRACTS. Summing raw prices counted a
          // "100" Discount as +100, so a 1000 receipt itemized as 500 + tip 200 − discount 100 +
          // tax 200 + other 200 (= 1000, matched in the modal) came back as a 1200 expense.
          const grandTotal =
            newItems.reduce((sum, row) => sum + (Number.parseFloat(row.price) || 0), 0) +
            newFees.reduce(
              (sum, row) => sum + signedChargeAmount(row.name, row.price, row.sign),
              0,
            );
          if (grandTotal > 0) setAmount(grandTotal.toFixed(2));
          // "Assign each item" ⇒ the bill splits BY AMOUNT among exactly the assigned members
          // (each one's items + share of the extras, scaled onto the breakdown's grand total so
          // the legs sum to the bill). It goes through the same `splitOverride` the Split field
          // writes, so the field reads "Amount", its modal reopens on those figures, and the save
          // path validates/rescales it like any hand-entered amount split. This used to be
          // dropped entirely, leaving the default equal split across the WHOLE group. "Split
          // total (evenly)" (`null`) voids only an assignment-derived split — a split the user
          // configured by hand is left alone; no choice (`undefined`) touches nothing.
          if (assignedShares) {
            const entries = assignedSharesToSplit(
              assignedShares,
              splitMembers.map((mem) => mem.id),
              Math.round(grandTotal * 100),
            );
            if (entries.length > 0) {
              setSplitOverride(entries);
              setSplitFromAssignment(true);
            }
          } else if (assignedShares === null && splitFromAssignment) {
            setSplitOverride(null);
            setSplitFromAssignment(false);
          }
        }}
      />
      <LocationModal
        open={locationOpen}
        onOpenChange={setLocationOpen}
        value={location}
        onSelect={setLocation}
      />
    </>
  );
}
